import {
  Component,
  OnInit,
  OnDestroy,
  DestroyRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { debounceTime, distinctUntilChanged, filter, map, Subject, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { EqualityValidationService, EqualityValidationError } from '../../../services/mbrs/equality-validation.service';
import { ValidationContextService } from '../../../services/mbrs/validation-context.service';
import { FormSchemaService } from '../../../services/mbrs/form-schema.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { FORM_CALCULATION_DEBOUNCE_MS } from '../../../utils/debounce-times.util';
import { SubmitForValidationComponent } from '../../common/submit-for-validation/submit-for-validation.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { SkeletonModule } from 'primeng/skeleton';
import { pickRole9SchemaFile } from '../../../services/mbrs/validators/role9-sub-classification.rules';
import { isCellVisible } from '../../../services/mbrs/validators/dimension-visibility.util';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { FieldLinkLabelComponent } from '../../common/field-link-label/field-link-label.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { enableDualColumnRowAndRevealVisible } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface SchemaControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    controlType: string;
    level?: string | number;
    readonly?: boolean;
    calculated?: boolean;
    rows?: number;
    dualColumn?: boolean;
    sectionBoundary?: number; // Marks the end of a collapsible section (level number)
    linkTo?: {
      // NEW
      role: string; // Role code (e.g., "210100")
      qname: string; // Target qname in source role
    };
    inheritFromRole9?: boolean; // LEGACY (keep for backward compatibility)
    inheritFromRole?: string; // LEGACY
  };
  required?: boolean;
  validators?: Array<{ name: string; value?: any }>;
  customValidations?: Array<{
    // NEW (Optional)
    type: string;
    message: string;
    rule?: any;
    condition?: any;
  }>;
}

interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: SchemaControl[];
}

@Component({
  selector: 'app-role9-210100-sub-classification',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    FieldLinkLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    Toast,
    SubmitForValidationComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    FieldLinkTargetDirective,
    SkeletonModule,
  ],
  providers: [MessageService],
  templateUrl: './role9-210100-sub-classification.component.html',
  styleUrls: ['./role9-210100-sub-classification.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Role9210100SubClassificationComponent
  implements OnInit, OnDestroy
{
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private equalityValidation = inject(EqualityValidationService);
  private validationContext = inject(ValidationContextService);
  private formSchemaService = inject(FormSchemaService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private envService = inject(EnvironmentService);
  private mappingStatus = inject(MappingStatusService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);

  form!: FormGroup;
  schema!: FormSchema;
  isLoading = signal(true);
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Sub-classification of Assets, Liabilities & Equity (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
    );
  }

  /**
   * Bound as a property, not a method reference, so the directive receives a
   * stable function identity — a `[fieldLinkIsSectionHeader]="isSectionHeader"`
   * binding would pass an unbound method whose `this` is undefined when called.
   */
  protected readonly isSectionHeaderFn = (control: any): boolean =>
    this.isSectionHeader(control);

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  private isLoadingState = false;
  private isCalculating = false;
  private calculatedTotalKeys: string[] = [];
  isValidationComplete = signal(false);

  /**
   * Internal version signal used under OnPush to notify the template that
   * non-signal mutable state (validationErrors, equalityValidationErrors,
   * expandedSections, rowVisibilityCache, editingRows) has changed.
   * Template-called helper methods read this signal at the top so that
   * bumping it re-evaluates all bindings that depend on those Maps/Sets.
   */
  private viewVersion = signal(0);

  private bumpView(): void {
    this.viewVersion.update((version) => version + 1);
  }

  // Track which sections are expanded (using Set for Golden Standard pattern)
  expandedSections = new Set<string>();

  // Performance optimization: Cache row visibility to avoid recalculating on every change detection
  private rowVisibilityCache = new Map<number, boolean>();

  // Performance optimization: Cache column visibility (recalculated only when needed)
  cachedShowConsolidatedCurrent = false;
  cachedShowConsolidatedPrevious = false;
  cachedShowSeparateCurrent = true;
  cachedShowSeparatePrevious = false;
  cachedTotalDataColumns = 0;

  // Edit mode tracking per row (key: qname, value: original values)
  private editingRows = new Map<string, { [key: string]: number | null }>();

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes. */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  // Validation error tracking per row (key: qname, value: array of error messages)
  private validationErrors = new Map<string, string[]>();
  private equalityValidationErrors = new Map<string, EqualityValidationError[]>(); // Track equality validation errors
  /** qnames that the last cross-sheet run (runEqualityValidations) wrote into
   *  validationErrors. Frozen between Ticks so validateSameSheet can clear the
   *  shared map without wiping the cross-sheet verdict mid-edit. */
  private crossSheetErrorQnames = new Set<string>();

  // Dynamic labels from Role-1 (via CrossReferenceService)
  get roundingUnit(): string {
    return this.crossRefService.getCurrencyWithRounding();
  }

  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  get periodHeaderPrevious(): string {
    return this.crossRefService.getFormattedFinancialYear('previous');
  }

  get totalFields(): number {
    if (!this.schema || !this.schema.controls) return 0;
    return (
      this.schema.controls.filter((control) => control.type === 'MonetaryItemType').length *
      4
    );
  }

  /**
   * The four (dimension × period) visibility cells for this sheet, derived by
   * the shared, config-driven resolver via CrossReferenceService.getVisibility().
   * The SAME resolver + central registry
   * (SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity → NATURE / PREP
   * gates) feeds the pure role9SubClassificationValidator, so the mounted
   * editor and the headless listing cannot drift on which columns are
   * shown/validated. Previously these getters read the strict
   * `crossRefService.isConsolidated()` / `isSubsequentPreparation()` directly
   * (unknown/unset → hidden); the shared resolver treats an unknown Nature /
   * prep type as "shown" — the same convergence applied to role 8 SFP. In a
   * real filing the Nature / prep fields are always populated, so the only
   * observable difference is the pre-hydration default (now: shown).
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
    );
  }

  /**
   * Check if Consolidated Current column should be displayed
   * Rules: Show for Consolidated (both 1st time and Subsequent)
   */
  get showConsolidatedCurrent(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Consolidated', 'CurrentYear');
  }

  /**
   * Check if Consolidated Previous column should be displayed
   * Rules: Show only for Consolidated + Subsequent
   */
  get showConsolidatedPrevious(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Consolidated', 'PreviousYear');
  }

  /**
   * Check if Separate Current column should be displayed
   * Rules: Always show (for both Separate and Consolidated)
   */
  get showSeparateCurrent(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Separate', 'CurrentYear');
  }

  /**
   * Check if Separate Previous column should be displayed
   * Rules: Show for Separate + Subsequent, or Consolidated + Subsequent
   */
  get showSeparatePrevious(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Separate', 'PreviousYear');
  }

  /**
   * Get total number of data columns to display
   * Used for colspan calculations in headers and section rows
   */
  get totalDataColumns(): number {
    let count = 0;
    if (this.showConsolidatedCurrent) count++;
    if (this.showConsolidatedPrevious) count++;
    if (this.showSeparateCurrent) count++;
    if (this.showSeparatePrevious) count++;
    return count;
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '9',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Purpose: RCA for empty-form-after-refresh repro.
    // Kept commented for the fan-out to other sheets.
    // console.warn('[role9-init] ngOnInit ENTER', { conversionId: this.stateService.getConversionId(), isHydratingMbrsData: this.stateService.isHydratingMbrsData() });
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    try {
      await this.loadSchema();
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // Purpose: RCA for empty-form-after-refresh repro.
      // Kept commented for the fan-out to other sheets.
      // console.warn('[role9-init] after loadSchema', { hasSchema: !!this.schema });
      
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // Purpose: RCA for empty-form-after-refresh repro.
      // Kept commented for the fan-out to other sheets.
      // console.warn('[role9-init] after buildForm', { controlCount: this.form ? Object.keys(this.form.controls).length : 0 });
      this.initializeExpandedSections();
      this.updateColumnVisibilityCache(); // Cache column visibility
      this.recalculateRowVisibility(); // Cache row visibility
      this.setupEditPipeline();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // Purpose: RCA for empty-form-after-refresh repro.
      // Kept commented for the fan-out to other sheets.
      // console.warn('[role9-init] before loadSavedState');
      
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupCrossSheetValidationListener();
      this.isLoading.set(false);
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // Purpose: RCA for empty-form-after-refresh repro.
      // Kept commented for the fan-out to other sheets.
      // console.error('[role9-init] ngOnInit THREW before completing', error);

      console.error('Error initializing Sub-classification form:', error);
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading.set(false);
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Purpose: RCA for empty-form-after-refresh repro.
    // Kept commented for the fan-out to other sheets.
    // console.warn('[role9-init] ngOnDestroy', { isHydratingMbrsData: this.stateService.isHydratingMbrsData() });
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load schema based on accounting standard (MFRS or MPERS)
   */
  private async loadSchema(): Promise<void> {
    try {
      // Wait for the retrieve-mbrs-data hydration to finish before selecting the
      // schema. On a page refresh the saved sheet is restored and this component
      // mounts WHILE hydration is still in flight, so the active filing-info slot
      // (which carries the accounting standard) is not populated yet. Picking the
      // schema from that empty slot would fall back to the MFRS default
      // (pickRole9SchemaFile('') === MFRS) and, for an MPERS filing, build a form
      // whose control names never match the MPERS values — leaving every field
      // blank after refresh. Awaiting here is a no-op once hydration has already
      // completed (e.g. when navigating to the sheet via the selector), so the
      // happy path is unaffected.
      await this.stateService.waitForMbrsHydration('role9');
      this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

      // Read from the active filing-info source (role0 in Phase 1, role1 in Phase 2).
      const filingData = this.stateService.getActiveFilingInfoData();

      const accountingStandard =
        filingData[
          'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'
        ] || '';

      // MFRS/MPERS schema selection is shared with role9SubClassificationValidator
      // via pickRole9SchemaFile (see role9-sub-classification.rules.ts).
      // Editing the selection rule here without updating the rules file will
      // trip the parity-check harness in dev.
      const schemaPath = `/assets/forms/${pickRole9SchemaFile(accountingStandard)}`;

      this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
      this.schema = await fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        schemaPath,
      );
      this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);

      // Preload schemas for sheets referenced by equality validation rules
      // so that error messages show field labels instead of raw qnames
      const isMFRS = this.crossRefService.isMFRS();
      const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';
      await this.validationContext.preloadReferencedSchemas(this.schema, currentTaxonomy);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      throw new Error('Schema must be loaded before building form');
    }

    const formControls: { [key: string]: FormControl } = {};

    this.schema.controls.forEach((control: SchemaControl) => {
      // Skip section headers
      if (control.type === 'SectionHeader') {
        return;
      }

      // TextBlock fields (single control, no XBRL dimensional naming)
      if (control.type === 'TextBlock') {
        formControls[control.qname] = new FormControl(null);
        return;
      }

      // MonetaryItemType fields with XBRL dimensional naming
      if (control.type === 'MonetaryItemType' && control.ui.dualColumn) {
        // All controls start as disabled by default (edit mode will enable them)
        const startsDisabled = true;

        // Create 4 controls: Consolidated Current, Consolidated Previous, Separate Current, Separate Previous
        ['Consolidated', 'Separate'].forEach((consolidatedOrSeparate) => {
          ['Current', 'Previous'].forEach((period) => {
            const controlName = this.getXbrlControlName(
              control.qname,
              consolidatedOrSeparate as any,
              period as any
            );
            const defaultValue = null;
            formControls[controlName] = new FormControl({
              value: defaultValue,
              disabled: startsDisabled,
            });
          });
        });
      }
    });

    this.form = new FormGroup(formControls);
  }

  /**
   * Get XBRL prefix based on schema (ifrs-full for MFRS, ifrs-smes for MPERS)
   */
  private getXbrlPrefix(): string {
    // Check if any control qname uses ifrs-smes or mpers (MPERS)
    const hasMpers = this.schema.controls.some(
      (control) =>
        control.qname.startsWith('ifrs-smes:') ||
        control.qname.startsWith('ssmt-mpers:') ||
        control.qname.includes('mpers')
    );

    if (hasMpers) {
      return 'ifrs-smes';
    }

    // Check if any control qname uses ifrs-full (MFRS)
    const hasMfrs = this.schema.controls.some(
      (control) =>
        control.qname.startsWith('ifrs-full:') || control.qname.includes('mfrs')
    );

    if (hasMfrs) {
      return 'ifrs-full';
    }

    // Default to MFRS (ifrs-full)
    return 'ifrs-full';
  }

  /**
   * Get XBRL control name with dimensional pattern
   * Format: {qname}[Consolidated|Separate][CurrentYear|PreviousYear]
   */
  getXbrlControlName(
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): string {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  }

  /**
   * SAME-SHEET validation — safe to run live during edit. Clears + rebuilds only
   * same-sheet error sources; leaves cross-sheet (equality) errors frozen in place.
   *
   * validateAgainstApiData() writes to validationErrors. runEqualityValidations()
   * ALSO merges into that shared map, so we cannot do a blanket clear(). Instead
   * we delete only the keys NOT in crossSheetErrorQnames (those were frozen by
   * the last validateCrossSheet run), then re-run the same-sheet validators.
   */
  private validateSameSheet(): void {
    const keysToDelete: string[] = [];
    this.validationErrors.forEach((_unusedMessages, key) => {
      if (!this.crossSheetErrorQnames.has(key)) keysToDelete.push(key);
    });
    keysToDelete.forEach((key) => this.validationErrors.delete(key));

    this.validateAgainstApiData();
    this.bumpView();
  }

  /**
   * CROSS-SHEET validation — runs only on Tick and from cross-sheet listeners.
   * Strips the previous cross-sheet contributions from the shared validationErrors
   * map (keeping same-sheet "does not match the extracted sum" warnings on the
   * same qname), clears tracking state, re-runs runEqualityValidations(), then
   * rebuilds crossSheetErrorQnames from the fresh equalityValidationErrors.
   */
  private validateCrossSheet(): void {
    this.crossSheetErrorQnames.forEach((qname) => {
      const errors = this.validationErrors.get(qname);
      if (!errors) return;
      const sameSheetOnly = errors.filter((message) => message.includes(EXTRACTED_SUM_MARKER));
      if (sameSheetOnly.length > 0) {
        this.validationErrors.set(qname, sameSheetOnly);
      } else {
        this.validationErrors.delete(qname);
      }
    });
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    this.runEqualityValidations();

    // Record which qnames runEqualityValidations wrote so the next
    // validateSameSheet pass leaves them frozen.
    this.equalityValidationErrors.forEach((_unusedErrors, qname) => this.crossSheetErrorQnames.add(qname));

    this.bumpView();
  }

  /**
   * Run all validations (same-sheet + cross-sheet) in sequence.
   * Used at init, full-reload, and onCancelConfirmed.
   */
  private runAllValidations(): void {
    this.validationErrors.clear();
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    this.validateAgainstApiData();
    this.runEqualityValidations();

    // Record cross-sheet keys for future validateSameSheet passes.
    this.equalityValidationErrors.forEach((_unusedErrors, qname) => this.crossSheetErrorQnames.add(qname));

    this.bumpView();
  }

  /**
   * Setup unified edit pipeline: debounce → calculate → validateSameSheet → validateCrossSheet → evaluateStatus.
   * No persist — commits happen only on Tick (saveEditMode). This makes "no commit
   * until Tick" structural rather than flag-gated. `validateCrossSheet` runs live
   * (display-only) so role8 SOFP equality errors surface inline immediately.
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState || this.isCalculating,
      calculate: () => this.calculateTotals(),
      validateSameSheet: () => this.validateSameSheet(),
      validateCrossSheet: () => this.validateCrossSheet(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * Calculate all total fields
   * Now uses CalculationService for centralized calculation logic
   */
  private calculateTotals(): void {
    this.isCalculating = true;

    // NEW: Use CalculationService for centralized calculation logic
    // Get current form data
    const formData = this.form.getRawValue();

    // Get XBRL prefix (ifrs-full for MFRS, ifrs-smes for MPERS)
    const xbrlPrefix = this.getXbrlPrefix() as 'ifrs-full' | 'ifrs-smes';

    // Call calculation service to get calculated totals
    const calculatedTotals = this.calculationService.calculateTotals_210100(
      formData,
      xbrlPrefix
    );

    // Register calculated keys so isSheetModified() skips them
    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
      Object.keys(calculatedTotals)
    );

    // Apply calculated totals to form controls
    Object.keys(calculatedTotals).forEach((controlName) => {
      const control = this.form.get(controlName);
      if (control) {
        control.setValue(calculatedTotals[controlName], { emitEvent: false });
        // Mark as pristine to prevent calculated fields from showing 'Edited' status
        control.markAsPristine();
      }
    });

    // ============================================================================
    // OLD COMPONENT-BASED CALCULATION LOGIC (DISABLED)
    // Migrated to CalculationService.calculateTotals_210100()
    // Kept for reference and potential rollback if needed
    // To re-enable, change `if (false)` to `if (true)` below
    // ============================================================================
    if (false) {
    ['Consolidated', 'Separate'].forEach((consolidatedOrSeparate) => {
      ['Current', 'Previous'].forEach((period) => {
        const getValue = (qname: string) => {
          const controlName = this.getXbrlControlName(
            qname,
            consolidatedOrSeparate as any,
            period as any
          );
          const value = this.form.get(controlName)?.value;
          // Explicitly convert to number to avoid string concatenation
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        };

        const setValue = (qname: string, value: number) => {
          const controlName = this.getXbrlControlName(
            qname,
            consolidatedOrSeparate as any,
            period as any
          );
          const control = this.form.get(controlName);
          if (control && control.value !== value) {
            control.setValue(value, { emitEvent: false });
            // Mark as pristine to prevent calculated fields from showing 'Edited' status
            control.markAsPristine();
          }
        };

        // Get prefix for qnames
        const prefix = this.getXbrlPrefix();
        const customPrefix =
          prefix === 'ifrs-full' ? 'ssmt-mfrs' : 'ssmt-mpers';
        const specialPrefix = 'ssmt';

        /* 
          Property, Plant and Equipment - MFRS and MPERS Calculations
        */
        //#region Property, Plant and Equipment

        // 1. Total Land = Sum of land types
        const totalLand =
          getValue(`${customPrefix}:FreeholdLand`) +
          getValue(`${customPrefix}:LongtermLeaseholdLand`) +
          getValue(`${customPrefix}:ShorttermLeaseholdLand`);
        setValue(`${prefix}:Land`, totalLand);

        // 2. Total Buildings = Sum of building types
        const totalBuildings =
          getValue(`${customPrefix}:BuildingOnFreeholdLand`) +
          getValue(`${customPrefix}:BuildingOnLongtermLeaseholdLand`) +
          getValue(`${customPrefix}:BuildingOnShorttermLeaseholdLand`) +
          getValue(`${customPrefix}:LeasedProperties`);
        setValue(`${prefix}:Buildings`, totalBuildings);

        // 3. Total Land and Buildings = Total Land + Total Buildings
        const totalLandAndBuildings = totalLand + totalBuildings;
        setValue(`${prefix}:LandAndBuildings`, totalLandAndBuildings);

        // MPERS
        if (prefix === 'ifrs-smes') {
          // 4. Total Property, Plant and Equipment = Sum of all PPE items
          const totalPPE =
            totalLandAndBuildings +
            getValue(`${prefix}:Machinery`) +
            getValue(`${prefix}:Vehicles`) +
            getValue(`${customPrefix}:OfficeEquipmentFixtureAndFittings`) +
            getValue(`${customPrefix}:PlantAndEquipment`) +
            getValue(`${prefix}:ConstructionInProgress`) +
            getValue(`${prefix}:OtherPropertyPlantAndEquipment`);
          setValue(`${prefix}:PropertyPlantAndEquipment`, totalPPE);
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          // 4. Total Vehicles = Sum of vehicle types
          const totalVehicles =
            getValue(`${customPrefix}:Vessels`) +
            getValue(`${prefix}:Aircraft`) +
            getValue(`${prefix}:MotorVehicles`) +
            getValue(`${customPrefix}:OtherVehicles`);
          setValue(`${prefix}:Vehicles`, totalVehicles);

          // 5. Total Property, Plant and Equipment = Sum of all PPE items
          const totalPPE =
            totalLandAndBuildings +
            totalVehicles +
            getValue(`${prefix}:Machinery`) +
            getValue(`${customPrefix}:PlantAndEquipment`) +
            getValue(`${customPrefix}:OfficeEquipmentFixtureAndFittings`) +
            getValue(`${customPrefix}:ComputerSoftwareAndHardware`) +
            getValue(`${prefix}:BearerPlants`) +
            getValue(`${customPrefix}:InfrastructureAndSiteFacilities`) +
            getValue(`${prefix}:MiningAssets`) +
            getValue(`${prefix}:OilAndGasAssets`) +
            getValue(`${customPrefix}:TangibleExplorationAndEvaluationAssets`) +
            getValue(`${customPrefix}:TelecommunicationEquipments`) +
            getValue(`${prefix}:ConstructionInProgress`) +
            getValue(`${prefix}:OtherPropertyPlantAndEquipment`);
          setValue(`${prefix}:PropertyPlantAndEquipment`, totalPPE);
        }

        //#endregion Property, Plant and Equipment

        /* 
          Investment properties - MFRS and MPERS Calculations
        */
        //#region Investment properties

        // MPERS
        if (prefix === 'ifrs-smes') {
          // 1. Total Investment Properties
          const freeholdLand = getValue(
            `${customPrefix}:InvestmentPropertyFreeholdLandAndBuilding`
          );
          const longtermLeasehold = getValue(
            `${customPrefix}:InvestmentPropertyLongtermLeaseholdLand`
          );
          const shorttermLeasehold = getValue(
            `${customPrefix}:InvestmentPropertyShorttermLeaseholdLand`
          );
          const buildings = getValue(
            `${customPrefix}:InvestmentPropertyBuildingUnderConstruction`
          );
          const otherInvestment = getValue(
            `${customPrefix}:OtherInvestmentProperty`
          );

          const totalInvestmentProperty =
            freeholdLand +
            longtermLeasehold +
            shorttermLeasehold +
            buildings +
            otherInvestment;

          setValue(`${prefix}:InvestmentProperty`, totalInvestmentProperty);
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          // 1. Total Investment Properties Completed = Freehold + Leasehold
          const totalInvestmentPropertyCompleted =
            getValue(
              `${customPrefix}:InvestmentPropertyFreeholdLandAndBuilding`
            ) +
            getValue(
              `${customPrefix}:InvestmentPropertyLeaseholdLandAndBuilding`
            );
          setValue(
            `${prefix}:InvestmentPropertyCompleted`,
            totalInvestmentPropertyCompleted
          );

          // 2. Total Investment Properties Under Construction = Building Under Construction
          const totalInvestmentPropertyUnderConstruction = getValue(
            `${customPrefix}:InvestmentPropertyBuildingUnderConstruction`
          );
          setValue(
            `${prefix}:InvestmentPropertyUnderConstructionOrDevelopment`,
            totalInvestmentPropertyUnderConstruction
          );

          // 3. Total Investment Properties = Completed + Under Construction + Other
          const totalInvestmentPropertyMFRS =
            totalInvestmentPropertyCompleted +
            totalInvestmentPropertyUnderConstruction +
            getValue(`${customPrefix}:OtherInvestmentProperty`);
          setValue(`${prefix}:InvestmentProperty`, totalInvestmentPropertyMFRS);
        }

        //#endregion Investment properties

        /* 
          Biological Assets - MFRS Calculations
        */
        // #region Biological Assets
        if (prefix === 'ifrs-full') {
          const totalBiologicalAssets =
            getValue(`${customPrefix}:ConsumableBiologicalAssets`) +
            getValue(`${customPrefix}:BearerBiologicalAssets`);
          setValue(`${prefix}:BiologicalAssets`, totalBiologicalAssets);
        }
        // #endregion Biological Assets

        /* 
          Intangible assets and goodwill - MFRS and MPERS Calculations
        */
        // #region Intangible assets and goodwill

        // MPERS
        if (prefix === 'ifrs-smes') {
          // 1. Total Intangible Assets Other Than Goodwill = Copyrights + Other
          const totalIntangiblesOtherThanGoodwill =
            getValue(
              `${prefix}:CopyrightsPatentsAndOtherIndustrialPropertyRightsServiceAndOperatingRights`
            ) + getValue(`${prefix}:OtherIntangibleAssets`);
          setValue(
            `${prefix}:IntangibleAssetsOtherThanGoodwill`,
            totalIntangiblesOtherThanGoodwill
          );

          // 2. Total Intangible Assets and Goodwill = Total Other Than Goodwill + Goodwill
          const totalIntangibleAssetsAndGoodwill =
            totalIntangiblesOtherThanGoodwill + getValue(`${prefix}:Goodwill`);
          setValue(
            `${prefix}:IntangibleAssetsAndGoodwill`,
            totalIntangibleAssetsAndGoodwill
          );
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          // 1. Total Intangible Assets Other Than Goodwill = Sum of all intangible assets except goodwill
          const totalIntangiblesOtherThanGoodwill =
            getValue(
              `${prefix}:CustomerrelatedIntangibleAssetsRecognisedAsOfAcquisitionDate`
            ) +
            getValue(`${prefix}:BrandNames`) +
            getValue(`${customPrefix}:CustomerRelationships`) +
            getValue(
              `${prefix}:CopyrightsPatentsAndOtherIndustrialPropertyRightsServiceAndOperatingRights`
            ) +
            getValue(`${prefix}:IntangibleAssetsUnderDevelopment`) +
            getValue(`${prefix}:IntangibleExplorationAndEvaluationAssets`) +
            getValue(`${prefix}:LicencesAndFranchises`) +
            getValue(`${prefix}:RecipesFormulaeModelsDesignsAndPrototypes`) +
            getValue(`${prefix}:ComputerSoftware`) +
            getValue(`${customPrefix}:SmallHolderRelationship`) +
            getValue(`${prefix}:OtherIntangibleAssets`);
          setValue(
            `${prefix}:IntangibleAssetsOtherThanGoodwill`,
            totalIntangiblesOtherThanGoodwill
          );

          // 2. Total Intangible Assets and Goodwill = Total Other Than Goodwill + Goodwill
          const totalIntangibleAssetsAndGoodwill =
            totalIntangiblesOtherThanGoodwill + getValue(`${prefix}:Goodwill`);
          setValue(
            `${prefix}:IntangibleAssetsAndGoodwill`,
            totalIntangibleAssetsAndGoodwill
          );
        }

        // #endregion Intangible assets and goodwill

        /* 
          Investments in Subsidiaries - MFRS and MPERS Calculations
        */
        // #region Investments in Subsidiaries

        // MPERS ONLY: Simple Investments in Subsidiaries Calculation
        if (prefix === 'ifrs-smes') {
          const totalInvestmentsInSubsidiaries =
            getValue(
              `${customPrefix}:InvestmentsInSubsidiariesUnquotedSharesNetOfImpairmentLosses`
            ) +
            getValue(
              `${customPrefix}:InvestmentInSubsidiariesQuotedSharesInMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInSubsidiariesQuotedSharesOutsideMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInSubsidiariesOtherInvestmentsNetOfImpairmentLosses`
            );
          // Intentional typo "Subisidiaries" — must match the SSM MPERS taxonomy element exactly or
          // Arelle stops matching this total. Do NOT "correct" to InvestmentInSubsidiaries (MPERS-only;
          // reverted in c933312). See docs/specifications/sheets/09b_sub-classification-assets-liabilities-equity-mpers/04_investments-subsidiaries.md
          setValue(
            `${customPrefix}:InvestmentInSubisidiaries`,
            totalInvestmentsInSubsidiaries
          );
        }

        // MFRS ONLY: Investments in Subsidiaries (with additional fields)
        if (prefix === 'ifrs-full') {
          const totalInvestmentsInSubsidiaries =
            getValue(
              `${customPrefix}:InvestmentInSubsidiariesUnquotedSharesInMalaysiaNetOfImpairmentLosses`
            ) +
            getValue(
              `${customPrefix}:InvestmentInSubsidiariesQuotedSharesInMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInSubsidiariesQuotedSharesOutsideMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInSubsidiariesFairValueAdjustmentsOnLoansAndAdvancesAndFinancialGuarantee`
            ) +
            getValue(
              `${customPrefix}:ContributionsToSubsidiariesNetOfImpairmentLosses`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInSubsidiariesOtherInvestmentsNetOfImpairmentLosses`
            );
          setValue(
            `${prefix}:InvestmentsInSubsidiaries`,
            totalInvestmentsInSubsidiaries
          );
        }
        // #endregion Investments in Subsidiaries

        /* 
          Investments in associates - MFRS and MPERS Calculations
        */
        // #region Investments in associates

        // MPERS
        if (prefix === 'ifrs-smes') {
          const totalInvestmentsInAssociates =
            getValue(
              `${customPrefix}:InvestmentsInAssociatesUnquotedSharesNetOfImpairmentLosses`
            ) +
            getValue(
              `${customPrefix}:InvestmentInAssociatesQuotedSharesInMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInAssociatesQuotedSharesOutsideMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInAssociatesShareOfPostAcquisitionProfitsAndReserves`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInAssociatesOtherInvestmentsNetOfImpairmentLosses`
            );
          setValue(
            `${prefix}:InvestmentsInAssociates`,
            totalInvestmentsInAssociates
          );
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          const totalInvestmentsInAssociates =
            getValue(
              `${customPrefix}:InvestmentInAssociatesUnquotedSharesInMalaysiaNetOfImpairmentLosses`
            ) +
            getValue(
              `${customPrefix}:InvestmentInAssociatesQuotedSharesInMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInAssociatesQuotedSharesOutsideMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInAssociatesFairValueAdjustmentsOnLoansAndAdvancesAndFinancialGuarantee`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInAssociatesShareOfPostAcquisitionProfitsAndReserves`
            ) +
            getValue(
              `${customPrefix}:InvestmentInAssociatesUnrealisedProfitOnTransactionsWithAssociates`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInAssociatesOtherInvestmentsNetOfImpairmentLosses`
            );
          setValue(
            `${prefix}:InvestmentsInAssociates`,
            totalInvestmentsInAssociates
          );
        }
        // #endregion Investments in associates

        /* 
          Investments in Joint Ventures - MFRS and MPERS Calculations
        */
        // #region Investments in Joint Ventures

        // MPERS
        if (prefix === 'ifrs-smes') {
          const totalInvestmentsInJointVentures =
            getValue(
              `${customPrefix}:InvestmentsInJointVentureUnquotedSharesNetOfImpairmentLosses`
            ) +
            getValue(
              `${customPrefix}:InvestmentInJointVenturesQuotedSharesInMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInJointVenturesQuotedSharesOutsideMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInJointVentureShareOfPostAcquisitionProfitsAndReserves`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInJointVentureOtherInvestment`
            );
          setValue(
            `${prefix}:InvestmentsInJointVentures`,
            totalInvestmentsInJointVentures
          );
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          const totalInvestmentsInJointVentures =
            getValue(
              `${customPrefix}:InvestmentInJointVenturesUnquotedSharesInMalaysiaNetOfImpairmentLosses`
            ) +
            getValue(
              `${customPrefix}:InvestmentInJointVenturesQuotedSharesInMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInJointVenturesQuotedSharesOutsideMalaysia`
            ) +
            getValue(
              `${customPrefix}:InvestmentInJointVenturesFairValueAdjustmentsOnLoansAdvancesAndFinancialGuarantee`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInJointVentureShareOfPostAcquisitionProfitsAndReserves`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInJointVentureUnrealisedProfitOnTransactionsWithJointVentures`
            ) +
            getValue(
              `${customPrefix}:InvestmentsInJointVentureOtherInvestment`
            );
          setValue(
            `${prefix}:InvestmentsInJointVentures`,
            totalInvestmentsInJointVentures
          );
        }

        // #endregion Investments in Joint Ventures

        /* 
          Trade and other non-current receivables - MFRS and MPERS Calculations
        */
        // #region Trade and Other Non-current Receivables

        // MPERS
        if (prefix === 'ifrs-smes') {
          // 1. Total non-current trade receivables (Level 4 total)
          const totalNoncurrentTradeReceivables =
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromRelatedParties`
            ) +
            getValue(`${customPrefix}:OtherNoncurrentTradeReceivables`);
          setValue(
            `${customPrefix}:NoncurrentTradeReceivables`,
            totalNoncurrentTradeReceivables
          );

          // 2. Total other non-current receivables due from related parties
          const totalOtherNoncurrentReceivablesDueFromRelatedParties =
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromOtherRelatedParties`
            );
          setValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromRelatedParties`,
            totalOtherNoncurrentReceivablesDueFromRelatedParties
          );

          // 3. Total non-current non-trade receivables
          const totalNoncurrentNontradeReceivables =
            getValue(
              `${customPrefix}:OtherNoncurrentNontradePrepaymentAndAccruedIncome`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentNontradeLeaseAndHirePurchaseReceivables`
            ) +
            getValue(`${customPrefix}:OtherNoncurrentNontradeMiscellaneous`);
          setValue(
            `${customPrefix}:OtherNoncurrentNontradeReceivables`,
            totalNoncurrentNontradeReceivables
          );

          // 4. Total other non-current receivables
          // MPERS: equals the non-trade total only (the related-parties subtotal
          // is still shown but excluded here per MBRS MPERS spec — see f63 formula
          // in 09b .../07_trade-and-other-non-current-receivables.md).
          const totalOtherNoncurrentReceivables =
            totalNoncurrentNontradeReceivables;
          setValue(
            `${customPrefix}:OtherNoncurrentReceivables`,
            totalOtherNoncurrentReceivables
          );

          // 5. Total trade and other non-current receivables (Level 3 total)
          // MPERS: trade + the related-parties subtotal (f58, excluded from f63)
          // + other non-current receivables (f63). See f64 formula in
          // 09b .../07_trade-and-other-non-current-receivables.md.
          const totalTradeAndOtherNoncurrentReceivables =
            totalNoncurrentTradeReceivables +
            totalOtherNoncurrentReceivablesDueFromRelatedParties +
            totalOtherNoncurrentReceivables;
          setValue(
            `${prefix}:NoncurrentReceivables`,
            totalTradeAndOtherNoncurrentReceivables
          );
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          // 1. Total non-current trade receivables (Level 4 total)
          const totalNoncurrentTradeReceivables =
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromCustomers`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:NoncurrentTradeReceivablesDueFromRelatedParties`
            ) +
            getValue(`${customPrefix}:OtherNoncurrentTradeReceivables`);
          setValue(
            `${prefix}:NoncurrentTradeReceivables`,
            totalNoncurrentTradeReceivables
          );

          // 2. Total other non-current receivables due from related parties
          const totalOtherNoncurrentReceivablesDueFromRelatedParties =
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:MiscellaneousOtherNoncurrentReceivablesDueFromRelatedCompanies`
            );
          setValue(
            `${customPrefix}:OtherNoncurrentReceivablesDueFromRelatedParties`,
            totalOtherNoncurrentReceivablesDueFromRelatedParties
          );

          // 3. Total non-current non-trade receivables
          const totalNoncurrentNontradeReceivables =
            getValue(
              `${customPrefix}:OtherNoncurrentNontradePrepaymentAndAccruedIncome`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentNontradeLeaseAndHirePurchaseReceivables`
            ) +
            getValue(`${customPrefix}:OtherNoncurrentNontradeMiscellaneous`);
          setValue(
            `${customPrefix}:OtherNoncurrentNontradeReceivables`,
            totalNoncurrentNontradeReceivables
          );

          // 4. Total other non-current receivables (sum of Level 5 totals)
          const totalOtherNoncurrentReceivables =
            totalOtherNoncurrentReceivablesDueFromRelatedParties +
            totalNoncurrentNontradeReceivables;
          setValue(
            `${prefix}:OtherNoncurrentReceivables`,
            totalOtherNoncurrentReceivables
          );

          // 5. Total trade and other non-current receivables (Level 3 total)
          const totalTradeAndOtherNoncurrentReceivables =
            totalNoncurrentTradeReceivables + totalOtherNoncurrentReceivables;
          setValue(
            `${prefix}:NoncurrentReceivables`,
            totalTradeAndOtherNoncurrentReceivables
          );

          // 6. Total non-current derivatives at fair value through profit or loss (Level 4 total)
          const totalNoncurrentDerivativesFVTPL =
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialAssetsForwardContract`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialAssetsOptions`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialAssetsSwaps`
            ) +
            getValue(
              `${customPrefix}:NoncurrentOtherDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`
            );
          setValue(
            `${customPrefix}:NoncurrentDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`,
            totalNoncurrentDerivativesFVTPL
          );

          // 7. Total non-current derivative financial assets (Level 3 total)
          const totalNoncurrentDerivativeFinancialAssets =
            totalNoncurrentDerivativesFVTPL +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialAssetsOthers`
            );
          setValue(
            `${prefix}:NoncurrentDerivativeFinancialAssets`,
            totalNoncurrentDerivativeFinancialAssets
          );
        }

        // #endregion Trade and Other Non-current Receivables

        /* 
          Inventories - MFRS and MPERS Calculations
        */
        // #region Inventories

        // MPERS
        if (prefix === 'ifrs-smes') {
          const totalInventories =
            getValue(`${customPrefix}:RawMaterials`) +
            getValue(`${prefix}:WorkInProgress`) +
            getValue(`${customPrefix}:FinishedGoods`) +
            getValue(`${customPrefix}:SpareParts`) +
            getValue(`${customPrefix}:OtherInventories`);
          setValue(`${prefix}:InventoriesTotal`, totalInventories);
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          const totalInventories =
            getValue(`${prefix}:RawMaterials`) +
            getValue(`${prefix}:WorkInProgress`) +
            getValue(`${prefix}:FinishedGoods`) +
            getValue(`${prefix}:SpareParts`) +
            getValue(`${prefix}:OtherInventories`);
          setValue(`${prefix}:InventoriesTotal`, totalInventories);
        }

        // #endregion Inventories

        /* 
          Trade and Other Current Receivables - MFRS and MPERS Calculations
        */
        // #region Trade and Other Current Receivables
        // MPERS
        if (prefix === 'ifrs-smes') {
          // 1. Total current trade receivables (Level 4 total)
          const totalCurrentTradeReceivables =
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromContractCustomers`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromRelatedParties`
            ) +
            getValue(`${customPrefix}:OtherCurrentTradeReceivables`);
          setValue(
            `${customPrefix}:CurrentTradeReceivables`,
            totalCurrentTradeReceivables
          );

          // 2. Total current other receivables due from related parties (Level 5 total)
          const totalOtherCurrentReceivablesDueFromRelatedParties =
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromOtherRelatedParties`
            );
          setValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromRelatedParties`,
            totalOtherCurrentReceivablesDueFromRelatedParties
          );

          // 3. Total current prepayments and current accrued income (Level 5 total)
          const totalCurrentPrepaymentsAndAccruedIncome =
            getValue(`${customPrefix}:OtherCurrentPrepayments`) +
            getValue(`${customPrefix}:OtherCurrentAccruedIncome`);
          setValue(
            `${customPrefix}:OtherCurrentPrepaymentsAndCurrentAccruedIncome`,
            totalCurrentPrepaymentsAndAccruedIncome
          );

          // 4. Total non-trade receivables (Level 5 total)
          const totalCurrentNontradeReceivables =
            getValue(
              `${customPrefix}:OtherCurrentNontradeFinanceIncomeReceivable`
            ) +
            getValue(`${customPrefix}:OtherCurrentNontradeDeposits`) +
            getValue(
              `${customPrefix}:OtherCurrentNontradeDividendReceivables`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentNontradeLeaseAndHirePurchaseReceivables`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentMiscellaneousNontradeReceivables`
            );
          setValue(
            `${customPrefix}:OtherCurrentNontradeReceivables`,
            totalCurrentNontradeReceivables
          );

          // 5. Total other current receivables (Level 4 total)
          const totalOtherCurrentReceivables =
            totalOtherCurrentReceivablesDueFromRelatedParties +
            totalCurrentPrepaymentsAndAccruedIncome +
            totalCurrentNontradeReceivables;
          setValue(
            `${customPrefix}:OtherCurrentReceivables`,
            totalOtherCurrentReceivables
          );

          // 6. Total trade and other current receivables (Level 3 total)
          const totalTradeAndOtherCurrentReceivables =
            totalCurrentTradeReceivables + totalOtherCurrentReceivables;
          setValue(
            `${prefix}:TradeAndOtherCurrentReceivables`,
            totalTradeAndOtherCurrentReceivables
          );
        }

        // MFRS
        if (prefix === 'ifrs-full') {
          // 1. Total current trade receivables (Level 4 total)
          const totalCurrentTradeReceivables =
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromCustomers`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromContractCustomers`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:CurrentTradeReceivablesDueFromRelatedParties`
            ) +
            getValue(
              `${customPrefix}:TradeReceivablesUnearnedCurrentCarryingCharges`
            ) +
            getValue(`${customPrefix}:OtherCurrentTradeReceivables`);
          setValue(
            `${prefix}:CurrentTradeReceivables`,
            totalCurrentTradeReceivables
          );

          // 2. Total other current receivables due from related parties (Level 5 total)
          const totalOtherCurrentReceivablesDueFromRelatedParties =
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentReceivablesDueFromOtherRelatedParties`
            );
          setValue(
            `${customPrefix}:OtherCurrentReceivablesDueFromRelatedParties`,
            totalOtherCurrentReceivablesDueFromRelatedParties
          );

          // 3. Total current prepayments and current accrued income (Level 5 total)
          const totalCurrentPrepaymentsAndAccruedIncome =
            getValue(`${customPrefix}:OtherCurrentPrepayments`) +
            getValue(`${customPrefix}:OtherCurrentAccruedIncome`);
          setValue(
            `${customPrefix}:OtherCurrentPrepaymentsAndCurrentAccruedIncome`,
            totalCurrentPrepaymentsAndAccruedIncome
          );

          // 4. Total current non-trade receivables (Level 5 total)
          const totalCurrentNontradeReceivables =
            getValue(
              `${customPrefix}:OtherCurrentNontradeFinanceIncomeReceivable`
            ) +
            getValue(`${customPrefix}:OtherCurrentNontradeDeposits`) +
            getValue(
              `${customPrefix}:OtherCurrentNontradeDividendReceivables`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentNontradeLeaseAndHirePurchaseReceivables`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentMiscellaneousNontradeReceivables`
            );
          setValue(
            `${customPrefix}:OtherCurrentNontradeReceivables`,
            totalCurrentNontradeReceivables
          );

          // 5. Total other current receivables (Level 4 total)
          const totalOtherCurrentReceivables =
            totalOtherCurrentReceivablesDueFromRelatedParties +
            totalCurrentPrepaymentsAndAccruedIncome +
            totalCurrentNontradeReceivables;
          setValue(
            `${prefix}:OtherCurrentReceivables`,
            totalOtherCurrentReceivables
          );

          // 6. Total trade and other current receivables (Level 3 total)
          const totalTradeAndOtherCurrentReceivables =
            totalCurrentTradeReceivables + totalOtherCurrentReceivables;
          setValue(
            `${prefix}:TradeAndOtherCurrentReceivables`,
            totalTradeAndOtherCurrentReceivables
          );

          // 7. Total current derivatives at fair value through profit or loss (Level 4 total)
          const totalCurrentDerivativesFVTPL =
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialAssetsForwardContract`
            ) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialAssetsOptions`
            ) +
            getValue(`${customPrefix}:CurrentDerivativeFinancialAssetsSwaps`) +
            getValue(
              `${customPrefix}:CurrentOtherDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`
            );
          setValue(
            `${customPrefix}:CurrentDerivativeFinancialAssetsAtFairValueThroughProfitOrLoss`,
            totalCurrentDerivativesFVTPL
          );

          // 8. Total current derivative financial assets (Level 3 total)
          const totalCurrentDerivativeFinancialAssets =
            totalCurrentDerivativesFVTPL +
            getValue(`${customPrefix}:CurrentDerivativeFinancialAssetsOthers`);
          setValue(
            `${prefix}:CurrentDerivativeFinancialAssets`,
            totalCurrentDerivativeFinancialAssets
          );
        }

        // #endregion Trade and Other Current Receivables

        /* 
          Cash and cash equivalents - MFRS and MPERS Calculations
        */
        // #region Cash and Cash Equivalents

        // 1. Total cash (Level 4 total)
        const totalCash =
          getValue(`${prefix}:CashOnHand`) +
          getValue(`${prefix}:BalancesWithBanks`);
        setValue(`${prefix}:Cash`, totalCash);

        // 2. Total cash equivalents (Level 4 total)
        // MPERS: 7-field calculation
        // MFRS: 6-field calculation
        const totalCashEquivalents =
          prefix === 'ifrs-full'
            ? getValue(
                `${customPrefix}:DepositsAndPlacementsWithLicensedBanks`
              ) +
              getValue(`${customPrefix}:DepositsPlacedWithOtherCorporations`) +
              getValue(
                `${customPrefix}:CashEquivalentsWithOtherFinancialInstitutions`
              ) +
              getValue(
                `${prefix}:ShorttermDepositsClassifiedAsCashEquivalents`
              ) +
              getValue(
                `${prefix}:ShorttermInvestmentsClassifiedAsCashEquivalents`
              ) +
              getValue(
                `${prefix}:BankingArrangementsClassifiedAsCashEquivalents`
              )
            : getValue(
                `${customPrefix}:DepositsAndPlacementsWithLicensedBanks`
              ) +
              getValue(`${customPrefix}:DepositsPlacedWithOtherCorporations`) +
              getValue(
                `${customPrefix}:FixedDepositsWithFinancialInstitutions`
              ) +
              getValue(
                `${customPrefix}:CashEquivalentsWithOtherFinancialInstitutions`
              ) +
              getValue(
                `${prefix}:ShorttermDepositsClassifiedAsCashEquivalents`
              ) +
              getValue(
                `${prefix}:ShorttermInvestmentsClassifiedAsCashEquivalents`
              ) +
              getValue(
                `${prefix}:BankingArrangementsClassifiedAsCashEquivalents`
              );
        setValue(`${prefix}:CashEquivalents`, totalCashEquivalents);

        // 3. Total cash and cash equivalents (Level 3 total)
        const totalCashAndCashEquivalents =
          totalCash +
          totalCashEquivalents +
          getValue(`${prefix}:OtherCashAndCashEquivalents`);
        setValue(
          `${specialPrefix}:CashAndBankBalances`,
          totalCashAndCashEquivalents
        );

        // MFRS ONLY: Other Non-current Assets
        if (prefix === 'ifrs-full') {
          const totalOtherNoncurrentAssets =
            getValue(`${customPrefix}:PrepaidRentalForBuildingAndFacilities`) +
            getValue(`${customPrefix}:PrepaidLandLease`) +
            getValue(`${customPrefix}:MiscellaneousOtherNoncurrentAssets`);
          setValue(
            `${prefix}:OtherNoncurrentAssets`,
            totalOtherNoncurrentAssets
          );
        }

        // #endregion Cash and Cash Equivalents

        /* 
          Issued capital - MFRS and MPERS Calculations
        */
        // #region Issued Capital

        const totalIssuedCapital =
          getValue(`${customPrefix}:CapitalFromOrdinaryShares`) +
          getValue(`${customPrefix}:CapitalFromRedeemablePreferenceShares`) +
          getValue(`${customPrefix}:CapitalFromNonredeemablePreferenceShares`);
        setValue(`${prefix}:IssuedCapital`, totalIssuedCapital);

        // #endregion Issued Capital

        /* 
          Reserves - MFRS and MPERS Calculations
        */
        // #region Reserves

        // 1. Total non-distributable reserves (Level 4 total)
        // MPERS: 4-field calculation
        // MFRS: Full calculation with all fields
        const totalNondistributableReserves =
          prefix === 'ifrs-smes'
            ? getValue(`${customPrefix}:CapitalReserve`) +
              getValue(`${prefix}:ReserveOfExchangeDifferencesOnTranslation`) +
              getValue(`${prefix}:RevaluationSurplus`) +
              getValue(`${customPrefix}:OtherNondistributableReserves`)
            : getValue(`${prefix}:CapitalReserve`) +
              getValue(`${prefix}:ReserveOfCashFlowHedges`) +
              getValue(`${prefix}:ReserveOfExchangeDifferencesOnTranslation`) +
              getValue(`${prefix}:ReserveOfSharebasedPayments`) +
              getValue(`${prefix}:RevaluationSurplus`) +
              getValue(`${prefix}:StatutoryReserve`) +
              getValue(`${prefix}:WarrantReserve`) +
              getValue(`${customPrefix}:OtherNondistributableReserves`);
        setValue(
          `${customPrefix}:NondistributableReserves`,
          totalNondistributableReserves
        );

        // 2. Total distributable reserves (Level 4 total)
        // MPERS: Only 1 field
        // MFRS: Full calculation with all fields
        const totalDistributableReserves =
          prefix === 'ifrs-smes'
            ? getValue(`${customPrefix}:OtherDistributableReserve`)
            : getValue(`${customPrefix}:FairValueReserves`) +
              getValue(
                `${customPrefix}:ReserveOfNoncurrentAssetsClassifiedAsHeldForSale`
              ) +
              getValue(`${customPrefix}:ConsolidatedReserve`) +
              getValue(`${customPrefix}:WarrantyReserve`) +
              getValue(`${customPrefix}:OtherDistributableReserve`);
        setValue(
          prefix === 'ifrs-smes'
            ? `${customPrefix}:DistributableReseves`
            : `${customPrefix}:DistributableReserve`,
          totalDistributableReserves
        );

        // 3. Total reserves (Level 3 total)
        const totalReserves =
          totalNondistributableReserves + totalDistributableReserves;
        setValue(`${prefix}:OtherReserves`, totalReserves);

        // #endregion Reserves

        /* 
          Equity - others components - MFRS and MPERS Calculations
        */
        // #region Equity - others components

        // 1. Total equity - other components
        // MPERS: 4 fields (no Head Office Accounts)
        // MFRS: 5 fields
        const totalEquityOtherComponents =
          prefix === 'ifrs-smes'
            ? getValue(`${customPrefix}:PerpetualSukuk`) +
              getValue(
                `${customPrefix}:EquityComponentOfIrredeemableConvertibleUnsecuredLoanStocks`
              ) +
              getValue(`${customPrefix}:EquityComponentOfPreferenceShares`) +
              getValue(
                `${customPrefix}:EquityComponentsOfOtherFinancialInstruments`
              )
            : getValue(`${customPrefix}:PerpetualSukuk`) +
              getValue(
                `${customPrefix}:EquityComponentOfIrredeemableConvertibleUnsecuredLoanStocks`
              ) +
              getValue(`${customPrefix}:EquityComponentOfPreferenceShares`) +
              getValue(`${customPrefix}:HeadOfficeAccounts`) +
              getValue(
                `${customPrefix}:EquityComponentsOfOtherFinancialInstruments`
              );
        setValue(
          `${customPrefix}:OtherComponentsOfEquity`,
          totalEquityOtherComponents
        );

        // #endregion Equity - others components

        /* 
          Loans and Borrowings - MFRS and MPERS Calculations
        */
        // #region Loans and Borrowings

        // 1. Total non-current loans and borrowings
        if (prefix === 'ifrs-smes') {
          // MPERS: Simplified flat structure (14 fields)
          const totalNoncurrentBorrowings =
            getValue(
              `${customPrefix}:NoncurrentPortionOfNoncurrentSecuredBankLoansReceived`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfNoncurrentUnsecuredBankLoansReceived`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredConvertibleNotes`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredConvertibleNotes`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfRedeemablePreferenceShares`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfFinanceLeaseLiabilities`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfLoanFromHoldingCompany`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfLoanFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfLoanFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfLoanFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfLoanFromOtherRelatedCompanies`
            ) +
            getValue(`${customPrefix}:OtherNoncurrentSecuredBankLoans`) +
            getValue(`${customPrefix}:OtherNoncurrentUnsecuredBankLoans`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfOtherNoncurrentBorrowings`
            );
          setValue(
            `${customPrefix}:NoncurrentBorrowings`,
            totalNoncurrentBorrowings
          );
        } else {
          // MFRS: Hierarchical structure with Level 4 totals
          // 1. Total non-current portion of non-current secured bank loans received (Level 4 total)
          const totalNoncurrentSecuredBankLoans =
            getValue(`${customPrefix}:NoncurrentPortionOfSecuredTermLoans`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredIslamicFinancingFacilities`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredHirePurchaseAndFinanceLeaseLiabilities`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredIslamicMediumTermNotes`
            ) +
            getValue(`${customPrefix}:NoncurrentPortionOfSecuredSukuk`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredRevolvingCreditAndOthers`
            ) +
            getValue(`${customPrefix}:OtherNoncurrentSecuredBankLoans`);
          setValue(
            `${prefix}:NoncurrentPortionOfNoncurrentSecuredBankLoansReceived`,
            totalNoncurrentSecuredBankLoans
          );

          // 2. Total non-current portion of non-current unsecured bank loans received (Level 4 total)
          const totalNoncurrentUnsecuredBankLoans =
            getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredTermLoans`) +
            getValue(
              `${customPrefix}:NoncurrentPortionofUnsecuredIrredeemableConvertibleUnsecuredLoanStocks`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredIslamicFinancingFacilities`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredHirePurchaseAndFinanceLeaseLiabilities`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredIslamicMediumTermNotes`
            ) +
            getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredSukuk`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredRevolvingCreditAndOthers`
            ) +
            getValue(`${customPrefix}:OtherNoncurrentUnsecuredBankLoans`);
          setValue(
            `${prefix}:NoncurrentPortionOfNoncurrentUnsecuredBankLoansReceived`,
            totalNoncurrentUnsecuredBankLoans
          );

          // 3. Total non-current portion of secured bonds/sukuk/loan stocks (Level 4 total)
          const totalSecuredBondsSukukLoanStock =
            getValue(`${customPrefix}:NoncurrentPortionOfSecuredBonds`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredSukukAsPerShariahCommittee`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredMediumTermNotes`
            ) +
            getValue(`${customPrefix}:NoncurrentPortionOfSecuredLoanStocks`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfSecuredPreferenceShares`
            );
          setValue(
            `${customPrefix}:NoncurrentPortionOfSecuredBondsSukukAndLoanStock`,
            totalSecuredBondsSukukLoanStock
          );

          // 4. Total non-current portion of unsecured bonds/sukuk/loan stocks (Level 4 total)
          const totalUnsecuredBondsSukukLoanStock =
            getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredBonds`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredSukukAsPerShariahCommittee`
            ) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredMediumTermNotes`
            ) +
            getValue(`${customPrefix}:NoncurrentPortionOfUnsecuredLoanStocks`) +
            getValue(
              `${customPrefix}:NoncurrentPortionOfUnsecuredPreferenceShares`
            );
          setValue(
            `${customPrefix}:NoncurrentPortionOfUnsecuredBondsSukukAndLoanStock`,
            totalUnsecuredBondsSukukLoanStock
          );

          // 5. Total other non-current borrowings (Level 4 total)
          const totalOtherNoncurrentBorrowings =
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfLoanFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfLoanFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfLoanFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfRedeemablePreferenceShares`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentPortionOfMiscellaneousBorrowings`
            );
          setValue(
            `${prefix}:NoncurrentPortionOfOtherNoncurrentBorrowings`,
            totalOtherNoncurrentBorrowings
          );

          // 6. Total non-current borrowings (Level 3 total - sum of all 5 Level 4 totals)
          const totalNoncurrentBorrowings =
            totalNoncurrentSecuredBankLoans +
            totalNoncurrentUnsecuredBankLoans +
            totalSecuredBondsSukukLoanStock +
            totalUnsecuredBondsSukukLoanStock +
            totalOtherNoncurrentBorrowings;
          setValue(`${prefix}:LongtermBorrowings`, totalNoncurrentBorrowings);
        }

        // #endregion Loans and Borrowings

        /* 
          Non-current employee benefits - MFRS and MPERS Calculations
        */
        // #region Non-current employee benefits

        // MPERS: 6 fields (no Retirement Benefits) vs MFRS: 7 fields
        const totalNoncurrentEmployeeBenefitLiabilities =
          prefix === 'ifrs-smes'
            ? getValue(
                `${customPrefix}:CashSettledShareBasedPaymentLiability`
              ) +
              getValue(`${customPrefix}:EmploymentTerminationBenefits`) +
              getValue(`${customPrefix}:ProvisionForUnconsumedLeave`) +
              getValue(`${customPrefix}:DefinedContributionPlan`) +
              getValue(`${customPrefix}:DefinedBenefitPlan`) +
              getValue(`${customPrefix}:OtherEmployeeBenefitLiabilities`)
            : getValue(
                `${customPrefix}:NoncurrentCashSettledShareBasedPaymentLiability`
              ) +
              getValue(`${customPrefix}:NoncurrentRetirementBenefits`) +
              getValue(
                `${customPrefix}:NoncurrentEmploymentTerminationBenefits`
              ) +
              getValue(`${customPrefix}:NoncurrentProvisionForConsumedLeave`) +
              getValue(`${customPrefix}:NoncurrentDefinedContributionPlan`) +
              getValue(`${customPrefix}:NoncurrentDefinedBenefitPlan`) +
              getValue(
                `${customPrefix}:NoncurrentOtherEmployeeBenefitLiabilities`
              );
        setValue(
          `${prefix}:NoncurrentProvisionsForEmployeeBenefits`,
          totalNoncurrentEmployeeBenefitLiabilities
        );

        // #endregion Non-current employee benefits

        /* 
          Non-current provisions - MFRS and MPERS Calculations
        */
        // #region Non-current provisions
        // MPERS: 6 fields (no Refund provision)
        // MFRS: 7 fields
        const totalNoncurrentProvisions =
          prefix === 'ifrs-smes'
            ? getValue(`${prefix}:LongtermWarrantyProvision`) +
              getValue(`${prefix}:LongtermRestructuringProvision`) +
              getValue(`${prefix}:LongtermLegalProceedingsProvision`) +
              getValue(`${prefix}:LongtermOnerousContractsProvision`) +
              getValue(
                `${customPrefix}:LongtermProvisionForDecommissioningRestorationAndRehabilitationCosts`
              ) +
              getValue(`${prefix}:LongtermMiscellaneousOtherProvisions`)
            : getValue(`${prefix}:LongtermWarrantyProvision`) +
              getValue(`${prefix}:LongtermRestructuringProvision`) +
              getValue(`${prefix}:LongtermLegalProceedingsProvision`) +
              getValue(`${prefix}:NoncurrentRefundsProvision`) +
              getValue(`${prefix}:LongtermOnerousContractsProvision`) +
              getValue(
                `${prefix}:LongtermProvisionForDecommissioningRestorationAndRehabilitationCosts`
              ) +
              getValue(`${prefix}:OtherLongtermProvisions`);
        setValue(`${prefix}:NoncurrentProvisions`, totalNoncurrentProvisions);

        //#endregion Non-current provisions

        /* 
          Trade and Other Non-current Payables - MFRS and MPERS Calculations
        */
        // #region Trade and Other Non-current Payables

        // 1. Total non-current trade payables (Level 4 total)
        // MPERS: 6 fields (no generic Trade payables)
        // MFRS: 7 fields
        const totalNoncurrentTradePayables =
          prefix === 'ifrs-smes'
            ? getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToHoldingCompany`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToSubsidiaries`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToAssociates`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToJointVentures`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToOtherRelatedParties`
              ) +
              getValue(`${customPrefix}:OtherNoncurrentTradePayables`)
            : getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToCustomers`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToHoldingCompany`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToSubsidiaries`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToAssociates`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToJointVentures`
              ) +
              getValue(
                `${customPrefix}:NoncurrentTradePayablesDueToOtherRelatedParties`
              ) +
              getValue(`${customPrefix}:OtherNoncurrentTradePayables`);
        setValue(
          `${prefix}:NoncurrentPayablesToTradeSuppliers`,
          totalNoncurrentTradePayables
        );

        // 2. Total other non-current payables due to related parties (Level 5 total)
        const totalOtherNoncurrentPayablesDueToRelatedParties =
          getValue(
            `${customPrefix}:OtherNoncurrentTradePayablesDueToHoldingCompany`
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentTradePayablesDueToSubsidiaries`
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentTradePayablesDueToAssociates`
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentTradePayablesDueToJointVentures`
          ) +
          getValue(
            `${customPrefix}:OtherNoncurrentPayablesDueToRelatedCompanies`
          );
        setValue(
          `${customPrefix}:OtherNoncurrentPayablesDueToRelatedParties`,
          totalOtherNoncurrentPayablesDueToRelatedParties
        );

        // 3. Total other payables due to non-controlling interests (Level 5 total)
        // MFRS only - MPERS does not have this section
        let totalOtherPayablesDueToNoncontrollingInterests = 0;
        if (prefix === 'ifrs-full') {
          totalOtherPayablesDueToNoncontrollingInterests =
            getValue(
              `${customPrefix}:OtherNoncurrentDividendPayablesDueToNoncontrollingInterests`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentLoansFromNoncontrollingInterests`
            ) +
            getValue(
              `${customPrefix}:OtherNoncurrentMiscellaneousPayablesDueToNoncontrollingInterests`
            );
          setValue(
            `${customPrefix}:OtherNoncurrentPayablesDueToNoncontrollingInterests`,
            totalOtherPayablesDueToNoncontrollingInterests
          );
        }

        // 4. Total non-current non-trade payables (Level 5 total)
        // MPERS: 4 fields (no Deposits) vs MFRS: 5 fields
        const totalNoncurrentNontradePayables =
          prefix === 'ifrs-smes'
            ? getValue(`${customPrefix}:NoncurrentNontradeDeferredIncome`) +
              getValue(`${customPrefix}:NoncurrentNontradeAccruals`) +
              getValue(`${customPrefix}:NoncurrentNontradeRetentionPayables`) +
              getValue(`${customPrefix}:OtherNoncurrentNontradePayables`)
            : getValue(`${customPrefix}:NoncurrentNontradeDeferredIncome`) +
              getValue(`${customPrefix}:NoncurrentNontradeAccruals`) +
              getValue(`${customPrefix}:NoncurrentNontradeRetentionPayables`) +
              getValue(
                `${customPrefix}:NoncurrentNontradeDepositsAndAdvancedBillings`
              ) +
              getValue(`${customPrefix}:OtherNoncurrentNontradePayables`);
        setValue(
          `${customPrefix}:NoncurrentNontradePayables`,
          totalNoncurrentNontradePayables
        );

        // 5. Total other non-current payables (Level 4 total)
        // MPERS: sum of 2 Level 5 totals (no non-controlling interests)
        // MFRS: sum of 3 Level 5 totals
        const totalOtherNoncurrentPayables =
          prefix === 'ifrs-smes'
            ? totalOtherNoncurrentPayablesDueToRelatedParties +
              totalNoncurrentNontradePayables
            : totalOtherNoncurrentPayablesDueToRelatedParties +
              totalOtherPayablesDueToNoncontrollingInterests +
              totalNoncurrentNontradePayables;
        setValue(
          prefix === 'ifrs-smes'
            ? `${customPrefix}:OtherNoncurrentPayables`
            : `${prefix}:OtherNoncurrentPayables`,
          totalOtherNoncurrentPayables
        );

        // 6. Total trade and other non-current payables (Level 3 total - sum of 2 Level 4 totals)
        const totalTradeAndOtherNoncurrentPayables =
          totalNoncurrentTradePayables + totalOtherNoncurrentPayables;
        setValue(
          `${prefix}:NoncurrentPayables`,
          totalTradeAndOtherNoncurrentPayables
        );

        // 7. MFRS only - MPERS does not have this section
        if (prefix === 'ifrs-full') {
          // Total non-current derivatives at fair value through profit or loss (Level 4 total)
          const totalNoncurrentDerivativeLiabilitiesFVTPL =
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesForwardContract`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesOptions`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesSwaps`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesOtherDerivatives`
            );
          setValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesAtFairValueThroughProfitOrLoss`,
            totalNoncurrentDerivativeLiabilitiesFVTPL
          );

          // Total non-current derivatives used for hedging (Level 4 total)
          const totalNoncurrentDerivativeLiabilitiesHedging =
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesForwardContractUsedForHedging`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitesOptionsUsedForHedging`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesSwapUsedForHedging`
            ) +
            getValue(
              `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesOtherDerivativesUsedForHedging`
            );
          setValue(
            `${customPrefix}:NoncurrentDerivativeFinancialLiabilitiesUsedForHedging`,
            totalNoncurrentDerivativeLiabilitiesHedging
          );

          // Total non-current derivative financial liabilities (Level 3 total)
          const totalNoncurrentDerivativeFinancialLiabilities =
            totalNoncurrentDerivativeLiabilitiesFVTPL +
            totalNoncurrentDerivativeLiabilitiesHedging +
            getValue(
              `${customPrefix}:OtherNoncurrentDerivativeFinancialLiabilities`
            );
          setValue(
            `${prefix}:NoncurrentDerivativeFinancialLiabilities`,
            totalNoncurrentDerivativeFinancialLiabilities
          );
        }

        // #endregion Trade and Other Non-current Payables

        /* 
          Current Borrowings - MFRS and MPERS Calculations
        */
        // #region Current Borrowings

        if (prefix === 'ifrs-smes') {
          // 1. Total current loans and borrowings
          // MPERS: Simplified flat structure (15 fields)
          const totalCurrentBorrowings =
            getValue(
              `${customPrefix}:CurrentSecuredBankLoansReceivedAndCurrentPortionOfNoncurrentSecuredBankLoansReceived`
            ) +
            getValue(
              `${customPrefix}:CurrentUnsecuredBankLoansReceivedAndCurrentPortionOfNoncurrentUnsecuredBankLoansReceived`
            ) +
            getValue(`${customPrefix}:CurrenPortionOfSecuredConvertibleNotes`) +
            getValue(
              `${customPrefix}:CurrentPortionOfUnsecuredConvertibleNotes`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredBankOverdrafts`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredBankOverdrafts`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfRedeemablePreferenceShares`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfFinanceLeaseLiabilities`
            ) +
            getValue(`${customPrefix}:CurrentPortionOfLoanFromHoldingCompany`) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfLoanFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfLoanFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfLoanFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfLoanFromOtherRelatedCompanies`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredOtherBankLoanReceived`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredOtherBankLoansReceived`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfMiscellaneousBorrowings`
            );
          setValue(`${prefix}:ShorttermBorrowings`, totalCurrentBorrowings);
        } else {
          // MFRS: Hierarchical structure with Level 4 totals
          // 1. Total current secured bank loans received and current portion (Level 4 total)
          const totalCurrentSecuredBankLoans =
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredBankersAcceptance`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredBankOverdrafts`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredTradeFinancingFacilities`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredTermLoans`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredIslamicFinancingFacilities`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredHirePurchaseAndFinanceLeaseLiabilities`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredIslamicMediumTermNotes`
            ) +
            getValue(`${customPrefix}:CurrentPortionOfNoncurrentSecuredSukuk`) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredRevolvingCreditAndOthers`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentSecuredOtherBankLoanReceived`
            );
          setValue(
            `${prefix}:CurrentSecuredBankLoansReceivedAndCurrentPortionOfNoncurrentSecuredBankLoansReceived`,
            totalCurrentSecuredBankLoans
          );

          // 2. Total current unsecured bank loans received and current portion (Level 4 total)
          const totalCurrentUnsecuredBankLoans =
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredBlockDiscountingPayables`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredTermLoans`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredIrredeemableConvertibleUnsecuredLoanStocks`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredIslamicFinancingFacilities`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredHirePurchaseAndFinanceLeaseLiabilities`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredIslamicMediumTermNotes`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredSukuk`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredRevolvingCreditAndOthers`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfNoncurrentUnsecuredOtherBankLoansReceived`
            );
          setValue(
            `${prefix}:CurrentUnsecuredBankLoansReceivedAndCurrentPortionOfNoncurrentUnsecuredBankLoansReceived`,
            totalCurrentUnsecuredBankLoans
          );

          // 3. Total current portion of secured bonds/sukuk/loan stocks (Level 4 total)
          const totalCurrentPortionSecuredBonds =
            getValue(`${customPrefix}:CurrentPortionOfSecuredBonds`) +
            getValue(
              `${customPrefix}:CurrentPortionOfSecuredSukukAsPerShariahCommittee`
            ) +
            getValue(`${customPrefix}:CurrentPortionOfSecuredMediumTermNotes`) +
            getValue(`${customPrefix}:CurrentPortionOfSecuredLoanStocks`) +
            getValue(`${customPrefix}:CurrentPortionOfSecuredPreferenceShares`);
          setValue(
            `${customPrefix}:CurrentPortionOfSecuredBondsSukukAndLoanStocks`,
            totalCurrentPortionSecuredBonds
          );

          // 4. Total current portion of unsecured bonds/sukuk/loan stocks (Level 4 total)
          const totalCurrentPortionUnsecuredBonds =
            getValue(`${customPrefix}:CurrentPortionOfUnsecuredBonds`) +
            getValue(
              `${customPrefix}:CurrentPortionOfUnsecuredSukukAsPerShariahCommittee`
            ) +
            getValue(
              `${customPrefix}:CurrentPortionOfUnsecuredMediumTermNotes`
            ) +
            getValue(`${customPrefix}:CurrentPortionOfUnsecuredLoanStocks`) +
            getValue(
              `${customPrefix}:CurrentPortionOfUnsecuredPreferenceShares`
            );
          setValue(
            `${customPrefix}:CurrentPortionOfUnsecuredBondsSukukAndLoanStock`,
            totalCurrentPortionUnsecuredBonds
          );

          // 5. Total other current borrowings (Level 4 total)
          const totalOtherCurrentBorrowings =
            getValue(`${customPrefix}:OtherCurrentPortionOfTradeLoans`) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfLoanFromSubsidiaries`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfLoanFromAssociates`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfLoanFromJointVentures`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfRedeemablePreferenceShares`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentPortionOfMiscellaneousBorrowings`
            );
          setValue(
            `${prefix}:OtherCurrentBorrowingsAndCurrentPortionOfOtherNoncurrentBorrowings`,
            totalOtherCurrentBorrowings
          );

          // 6. Total current borrowings (Level 3 total - sum of all 5 Level 4 totals)
          const totalCurrentBorrowings =
            totalCurrentSecuredBankLoans +
            totalCurrentUnsecuredBankLoans +
            totalCurrentPortionSecuredBonds +
            totalCurrentPortionUnsecuredBonds +
            totalOtherCurrentBorrowings;
          setValue(`${prefix}:ShorttermBorrowings`, totalCurrentBorrowings);
        }

        // #endregion Current Borrowings

        /*
          Current employee benefits - MPERS Calculations
          Current employee benefits liabilities - MFRS Calculations
        */
        // #region Current employee benefits

        // Total current employee benefit liabilities (Level 3 total)
        // MPERS: 6 fields (no Retirement Benefits) vs MFRS: 7 fields
        const totalCurrentEmployeeBenefitLiabilities =
          prefix === 'ifrs-smes'
            ? getValue(
                `${customPrefix}:CurrentCashSettledShareBasedPaymentLiability`
              ) +
              getValue(`${customPrefix}:CurrentEmploymentTerminationBenefits`) +
              getValue(`${customPrefix}:CurrentProvisionForUnconsumedLeave`) +
              getValue(`${customPrefix}:CurrentDefinedContributionPlan`) +
              getValue(`${customPrefix}:CurrentDefinedBenefitPlan`) +
              getValue(`${customPrefix}:CurrentOtherEmployeeBenefitLiabilities`)
            : getValue(
                `${customPrefix}:CurrentCashSettledShareBasedPaymentLiability`
              ) +
              getValue(`${customPrefix}:CurrentRetirementBenefits`) +
              getValue(`${customPrefix}:CurrentEmploymentTerminationBenefits`) +
              getValue(`${customPrefix}:CurrentProvisionForUnconsumedLeave`) +
              getValue(`${customPrefix}:CurrentDefinedContributionPlan`) +
              getValue(`${customPrefix}:CurrentDefinedBenefitPlan`) +
              getValue(
                `${customPrefix}:CurrentOtherEmployeeBenefitLiabilities`
              );
        setValue(
          `${prefix}:CurrentProvisionsForEmployeeBenefits`,
          totalCurrentEmployeeBenefitLiabilities
        );

        // #endregion Current employee benefits

        /*
          Current provision - MPERS and MFRS Calculations
        */
        // #region Current provisions

        // 1. Total current provisions (Level 3 total)
        // MPERS: 6 fields (no Refunds provision) vs MFRS: 7 fields
        const totalCurrentProvisions =
          prefix === 'ifrs-smes'
            ? getValue(`${prefix}:ShorttermWarrantyProvision`) +
              getValue(`${prefix}:ShorttermRestructuringProvision`) +
              getValue(`${prefix}:ShorttermLegalProceedingsProvision`) +
              getValue(`${prefix}:ShorttermOnerousContractsProvision`) +
              getValue(
                `${customPrefix}:ShorttermProvisionForDecommissioningRestorationAndRehabilitationCosts`
              ) +
              getValue(`${prefix}:OtherShorttermProvisions`)
            : getValue(`${prefix}:ShorttermWarrantyProvision`) +
              getValue(`${prefix}:ShorttermRestructuringProvision`) +
              getValue(`${prefix}:ShorttermLegalProceedingsProvision`) +
              getValue(`${prefix}:CurrentRefundsProvision`) +
              getValue(`${prefix}:ShorttermOnerousContractsProvision`) +
              getValue(
                `${prefix}:ShorttermProvisionForDecommissioningRestorationAndRehabilitationCosts`
              ) +
              getValue(`${prefix}:OtherShorttermProvisions`);
        setValue(`${prefix}:CurrentProvisions`, totalCurrentProvisions);

        // #endregion Current provisions

        /*
          Trade and Other Current Payables - MPERS and MFRS Calculations
        */
        // #region Trade and Other Current Payables

        // 1. Total current trade payables (Level 4 total)
        // MPERS: 7 fields (no generic Trade payables) vs MFRS: 8 fields
        const totalCurrentTradePayables =
          prefix === 'ifrs-smes'
            ? getValue(
                `${customPrefix}:CurrentTradePayableDueToContractCustomer`
              ) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToHoldingCompany`
              ) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToSubsidiaries`
              ) +
              getValue(`${customPrefix}:CurrentTradePayablesDueToAssociates`) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToJointVentures`
              ) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToRelatedCompanies`
              ) +
              getValue(`${customPrefix}:OtherCurrentTradePayables`)
            : getValue(`${customPrefix}:CurrentTradePayablesDueToCustomers`) +
              getValue(
                `${customPrefix}:CurrentTradePayableDueToContractCustomer`
              ) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToHoldingCompany`
              ) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToSubsidiaries`
              ) +
              getValue(`${customPrefix}:CurrentTradePayablesDueToAssociates`) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToJointVentures`
              ) +
              getValue(
                `${customPrefix}:CurrentTradePayablesDueToRelatedCompanies`
              ) +
              getValue(`${customPrefix}:OtherCurrentTradePayables`);
        setValue(
          `${prefix}:TradeAndOtherCurrentPayablesToTradeSuppliers`,
          totalCurrentTradePayables
        );

        // 2. Total other current payables due to related parties (Level 5 total)
        const totalOtherCurrentPayablesDueToRelatedParties =
          getValue(`${customPrefix}:OtherCurrentPayablesDueToHoldingCompany`) +
          getValue(`${customPrefix}:OtherCurrentPayablesDueToSubsidiaries`) +
          getValue(`${customPrefix}:OtherCurrentPayablesDueToAssociates`) +
          getValue(`${customPrefix}:OtherCurrentPayablesDueToJointVentures`) +
          getValue(
            `${customPrefix}:OtherCurrentPayablesDueToOtherRelatedParties`
          );
        setValue(
          `${customPrefix}:OtherCurrentPayablesDueToRelatedParties`,
          totalOtherCurrentPayablesDueToRelatedParties
        );

        // 3. Total other payables due to non-controlling interests (Level 5 total)
        // MFRS only - MPERS does not have this section
        let totalOtherPayablesDueToNonControllingInterests = 0;
        if (prefix === 'ifrs-full') {
          totalOtherPayablesDueToNonControllingInterests =
            getValue(
              `${customPrefix}:OtherCurrentDividendPayableToNoncontrollingInterest`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentLoansFromNoncontrollingInterest`
            ) +
            getValue(
              `${customPrefix}:OtherCurrentMiscellaneousPayableDueToNoncontrollingInterest`
            );
          setValue(
            `${customPrefix}:OtherCurrentPayablesDueToNoncontrollingInterests`,
            totalOtherPayablesDueToNonControllingInterests
          );
        }

        // 4. Total current non-trade payables (Level 5 total)
        // MPERS: 4 fields vs MFRS: 8 fields
        const totalCurrentNontradePayables =
          prefix === 'ifrs-smes'
            ? getValue(`${customPrefix}:CurrentNontradeDeferredIncome`) +
              getValue(`${customPrefix}:CurrentNontradeAccruals`) +
              getValue(`${customPrefix}:CurrentNontradeRetentionPayable`) +
              getValue(`${customPrefix}:OtherCurrentNontradePayables`)
            : getValue(`${customPrefix}:CurrentNontradeAccruals`) +
              getValue(`${customPrefix}:CurrentNontradeRetentionPayable`) +
              getValue(`${customPrefix}:CurrentNontradeDeferredIncome`) +
              getValue(
                `${customPrefix}:CurrentNontradeDepositsAndAdvancedBillings`
              ) +
              getValue(`${customPrefix}:CurrentNontradeFinancingCosts`) +
              getValue(`${customPrefix}:CurrentNontradeDividendPayable`) +
              getValue(`${customPrefix}:CurrentNontradeInterestPayable`) +
              getValue(`${customPrefix}:OtherCurrentNontradePayables`);
        setValue(
          `${customPrefix}:CurrentNontradePayables`,
          totalCurrentNontradePayables
        );

        // 5. Total other current payables (Level 4 total)
        // MPERS: sum of 2 Level 5 totals, MFRS: sum of 3 Level 5 totals
        const totalOtherCurrentPayables =
          prefix === 'ifrs-smes'
            ? totalOtherCurrentPayablesDueToRelatedParties +
              totalCurrentNontradePayables
            : totalOtherCurrentPayablesDueToRelatedParties +
              totalOtherPayablesDueToNonControllingInterests +
              totalCurrentNontradePayables;
        setValue(
          prefix === 'ifrs-smes'
            ? `${customPrefix}:OtherCurrentPayables`
            : `${prefix}:OtherCurrentPayables`,
          totalOtherCurrentPayables
        );

        // 6. Total trade and other current payables (Level 3 total - sum of 2 Level 4 totals)
        const totalTradeAndOtherCurrentPayables =
          totalCurrentTradePayables + totalOtherCurrentPayables;
        setValue(
          `${prefix}:TradeAndOtherCurrentPayables`,
          totalTradeAndOtherCurrentPayables
        );

        // #endregion Trade and Other Current Payables

        /*
          Current Derivative Financial Liabilities - MFRS Calculations
        */
        // #region Current Derivative Financial Liabilities
        // MFRS only - MPERS does not have this section
        if (prefix === 'ifrs-full') {
          // 1. Total current derivatives at fair value through profit or loss (Level 4 total)
          const totalCurrentDerivativeLiabilitiesFVTPL =
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesForwardContract`
            ) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOptions`
            ) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesSwap`
            ) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOthers`
            );
          setValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesAtFairValueThroughProfitOrLoss`,
            totalCurrentDerivativeLiabilitiesFVTPL
          );

          // 2. Total current derivatives used for hedging (Level 4 total)
          const totalCurrentDerivativeLiabilitiesHedging =
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesForwardContractUsedForHedging`
            ) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOptionsUsedForHedging`
            ) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesSwapUsedForHedging`
            ) +
            getValue(
              `${customPrefix}:CurrentDerivativeFinancialLiabilitiesOthersUsedForHedging`
            );
          setValue(
            `${customPrefix}:CurrentDerivativeFinancialLiabilitiesUsedForHedging`,
            totalCurrentDerivativeLiabilitiesHedging
          );

          // 3. Total current derivative financial liabilities (Level 3 total)
          const totalCurrentDerivativeFinancialLiabilities =
            totalCurrentDerivativeLiabilitiesFVTPL +
            totalCurrentDerivativeLiabilitiesHedging +
            getValue(
              `${customPrefix}:OtherCurrentDerivativeFinancialLiabilities`
            );
          setValue(
            `${prefix}:CurrentDerivativeFinancialLiabilities`,
            totalCurrentDerivativeFinancialLiabilities
          );
        }

        // #endregion Current Derivative Financial Liabilities
      });
    });
    } // END: if (false) - Old calculation logic disabled
    // ============================================================================

    this.isCalculating = false;
  }

  /**
   * Load saved state from state service
   * On page refresh, loads original API data from validation baseline
   * On subsequent navigation, loads saved/modified state
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);

    // Refresh-discard is now enforced centrally by FilingFormStateService:
    // on the first setConversionId() call of each JS context the modified
    // cache is reset to the original (backend) cache, so getSheetData() here
    // already returns pristine backend values after a refresh. The previous
    // role-local "load from validationBaseline on reload" branch has been
    // removed to eliminate duplication and prevent the two code paths from
    // drifting apart.

    // Load saved state (modified values) during navigation
    const savedData = this.stateService.getSheetData(
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
    );

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Purpose: RCA for empty-form-after-refresh repro.
    // Kept commented for the fan-out to other sheets.
    // console.warn('[role9-loadSavedState]', { conversionId: this.stateService.getConversionId(), savedKeyCount: savedData ? Object.keys(savedData).length : 0, isHydratingMbrsData: this.stateService.isHydratingMbrsData() });

    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });

      // IMPORTANT: Mark form as pristine after loading to prevent showing 'Edited' status on initial load
      this.form.markAsPristine();

      setTimeout(() => {
        this.calculateTotals();
        this.runAllValidations();
        // Mark as pristine again after calculations to ensure clean state on load
        this.form.markAsPristine();
        this.isLoadingState = false;
        this.isValidationComplete.set(true);

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);
        }, 600);
      }, 100);
    } else {
      // Still calculate totals and run validation in case there's API data to validate against
      setTimeout(() => {
        this.calculateTotals(); // Calculate totals so calculated fields show 0.00 instead of blank
        this.runAllValidations();
        this.isLoadingState = false;
        this.isValidationComplete.set(true);

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);
        }, 600);
      }, 100);
    }
  }

  /**
   * Cross-sheet validation listener.
   *
   * Role-9 reads filingPrerequisites (Role-0) at L370 inside
   * validateAgainstApiData (currency / consolidation / preparation type
   * influence which API baseline is used). Per the role0-cascade rule,
   * any change to filingPrerequisites must trigger a local re-validation.
   *
   * Two layers, mirroring the golden pattern (role-19):
   *   - modifiedState$ + hash + distinctUntilChanged → covers same-tab
   *     SPA navigation and (incidentally) the local arm of remote events.
   *   - remoteUpdateApplied$ → cross-tab safety net to guarantee
   *     re-validation when a sibling tab edits filingPrerequisites and
   *     the user is sitting on this sheet at the time of arrival.
   */
  private setupCrossSheetValidationListener(): void {
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
        })),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isLoadingState) {
          this.validateCrossSheet();
          this.evaluateSheetStatus();
          this.bumpView();
        }
      });

    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role9-subClassificationOfAssetsLiabilitiesEquity',
          //   sourceSheetKey: sheetKey,
          // });
        }

        // Own-slice change -> repaint this sheet's fields. Other-slice change ->
        // fall through to the cross-sheet revalidation tail only, as before.
        if (changedSheets.includes(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity))
          this.adoptRemoteState();

        this.validateCrossSheet();
        this.evaluateSheetStatus();
        this.bumpView();
      });
  }

  /**
   * Cross-tab adoption for role9 (sub-classification). Rows currently open in
   * edit mode are skipped — the user's in-flight work wins until they tick
   * (local overwrites and broadcasts) or press X (the row lands on the
   * adopted value; see `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.form,
      this.stateService.getSheetData(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  /**
   * Check if control should display (no TextBlock fields in this form)
   */
  shouldDisplayField(control: SchemaControl): boolean {
    return false; // No TextBlock fields in this form
  }

  /**
   * Check if control is monetary item (for table display)
   */
  isMonetaryItem(control: SchemaControl): boolean {
    return (
      control.type === 'MonetaryItemType' && control.ui.dualColumn === true
    );
  }

  /**
   * Check if control is section header
   */
  isSectionHeader(control: SchemaControl): boolean {
    return control.type === 'SectionHeader';
  }

  /**
   * Get header level class (handles both number and string levels)
   */
  getHeaderClass(level?: number | string): string {
    const numLevel =
      typeof level === 'string' ? parseInt(level.replace('h', '')) : level;
    if (numLevel === 1) return 'header-level-1';
    if (numLevel === 2) return 'header-level-2';
    if (numLevel === 3) return 'header-level-3';
    if (numLevel === 4) return 'header-level-4';
    if (numLevel === 5) return 'header-level-5';
    return '';
  }

  /**
   * Check if field is calculated/readonly
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory`, so this sheet answers the
   * question the same way as the other 16.
   *
   * **This sheet is why "no mandatory fields today" is not a reason to skip
   * wiring the marker.** When the marker seam rolled out (2026-08-11) this
   * sheet declared zero mandatory controls, so it was classified "nothing to
   * do" and left unwired. Hours later the PO's enumerated totals list flagged
   * its calculated totals — and the flags landed with nowhere to render, so
   * the `*` silently did not appear until smoke testing caught it. Wire the
   * marker whenever a sheet renders schema-driven labels, whatever its current
   * mandatory count. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves;
   * same-sheet `selfData` is deliberately NOT passed, because this sheet keys
   * its values per cell (`qname[Dimension][Period]`) rather than by bare
   * qname, so a same-sheet lookup would silently miss. Add it — with the right
   * key shape — if a same-sheet conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: SchemaControl): boolean {
    return control.ui.calculated === true || control.ui.readonly === true;
  }

  /**
   * Initialize all collapsible sections (Level 1, 2, 3, 4, 5) as expanded by default (Golden Standard pattern)
   */
  private initializeExpandedSections(): void {
    if (!this.schema) return;

    this.schema.controls.forEach((control: SchemaControl) => {
      if (
        control.ui.level === 1 ||
        control.ui.level === 'h1' ||
        control.ui.level === 2 ||
        control.ui.level === 'h2' ||
        control.ui.level === 3 ||
        control.ui.level === 'h3' ||
        control.ui.level === 4 ||
        control.ui.level === 'h4' ||
        control.ui.level === 5 ||
        control.ui.level === 'h5'
      ) {
        this.expandedSections.add(control.qname);
      }
    });
  }

  /**
   * Toggle expand/collapse state for a section (Golden Standard pattern)
   */
  toggleSection(sectionQname: string): void {
    if (this.expandedSections.has(sectionQname)) {
      this.expandedSections.delete(sectionQname);
    } else {
      this.expandedSections.add(sectionQname);
    }
    // Recalculate visibility cache after toggling
    this.recalculateRowVisibility();
    this.bumpView();
  }

  /**
   * Check if a section is expanded (Golden Standard pattern)
   */
  isSectionExpanded(sectionQname: string): boolean {
    this.viewVersion();
    return this.expandedSections.has(sectionQname);
  }

  /**
   * Helper to normalize level to a number
   */
  private getNormalizedLevel(level: number | string | undefined): number {
    if (typeof level === 'string') {
      return parseInt(level.replace('h', ''));
    }
    return level || 0;
  }

  /**
   * TrackBy function for ngFor performance optimization
   */
  trackByQname(index: number, control: SchemaControl): string {
    return control.qname;
  }

  /**
   * Update cached column visibility values (call when filing info changes)
   */
  private updateColumnVisibilityCache(): void {
    // Derive from the SAME shared resolver the getters use (and the pure
    // validator), so the cached *ngIf flags can never disagree with
    // showConsolidatedCurrent / showSeparatePrevious / etc.
    const cells = this.dimensionVisibility;
    this.cachedShowConsolidatedCurrent = isCellVisible(cells, 'Consolidated', 'CurrentYear');
    this.cachedShowConsolidatedPrevious = isCellVisible(cells, 'Consolidated', 'PreviousYear');
    this.cachedShowSeparateCurrent = isCellVisible(cells, 'Separate', 'CurrentYear');
    this.cachedShowSeparatePrevious = isCellVisible(cells, 'Separate', 'PreviousYear');

    // Calculate total columns
    let count = 0;
    if (this.cachedShowConsolidatedCurrent) count++;
    if (this.cachedShowConsolidatedPrevious) count++;
    if (this.cachedShowSeparateCurrent) count++;
    if (this.cachedShowSeparatePrevious) count++;
    this.cachedTotalDataColumns = count;
  }

  /**
   * Recalculate row visibility cache for all rows
   * Called when sections are expanded/collapsed
   */
  private recalculateRowVisibility(): void {
    if (!this.schema) return;

    this.rowVisibilityCache.clear();
    this.schema.controls.forEach((control, index) => {
      this.rowVisibilityCache.set(index, this.calculateRowVisibility(control, index));
    });
  }

  /**
   * Get cached row visibility (O(1) lookup instead of O(n) calculation)
   */
  shouldShowRow(control: SchemaControl, index: number): boolean {
    this.viewVersion();
    // Return cached value if available
    if (this.rowVisibilityCache.has(index)) {
      return this.rowVisibilityCache.get(index)!;
    }
    // Fallback to calculation (should rarely happen after initialization)
    return this.calculateRowVisibility(control, index);
  }

  /**
   * Calculate if a row should be shown based on hierarchical parent section expansion
   * Traverses up the actual parent chain by finding headers with progressively lower levels.
   * - Level 1: Always visible
   * - Level 2+: Visible if immediate parent is expanded (and that parent's visibility cascades)
   * - Data rows: Visible if immediate parent section is expanded (and cascades up)
   */
  private calculateRowVisibility(control: SchemaControl, index: number): boolean {
    if (!this.schema) return true;

    const controlLevel = this.getNormalizedLevel(control.ui.level);

    // Level 1 headers are always visible
    if (controlLevel === 1) {
      return true;
    }

    // Find actual parent section headers by traversing up the hierarchy
    // For section headers (level 2-5), look for parents with level < controlLevel
    // For data rows (level 0), look for any section header as immediate parent
    let maxLevelToFind = controlLevel > 0 && controlLevel <= 5 ? controlLevel - 1 : 5;

    for (let controlIndex = index - 1; controlIndex >= 0; controlIndex--) {
      const prevControl = this.schema.controls[controlIndex];

      // Honor section-boundary markers carried on calculated-total rows.
      // A boundary at level N marks the END of every collapsible sub-section
      // at level >= N, so the current row cannot be a descendant of any header
      // at level >= N that physically precedes the boundary. Lowering
      // maxLevelToFind here is what stops the no-level PPE line items
      // (Machinery … Total PPE) from being mis-attributed to the just-closed
      // "Vehicles" (MFRS, level 4) / "Buildings" (MPERS, level 5) sub-section.
      // Boundaries only ever lower the threshold, so this can never newly hide
      // a row — it can only release rows that were wrongly hidden.
      const boundary = prevControl.ui.sectionBoundary;
      if (boundary != null && boundary <= maxLevelToFind) {
        maxLevelToFind = boundary - 1;
      }

      if (prevControl.type !== 'SectionHeader') continue;

      const prevLevel = this.getNormalizedLevel(prevControl.ui.level);

      // Only consider headers that could be actual parents (level <= maxLevelToFind)
      if (prevLevel >= 1 && prevLevel <= maxLevelToFind) {
        // This is a parent - check if it's expanded
        if (!this.isSectionExpanded(prevControl.qname)) {
          return false;
        }

        // Now look for the next higher parent (lower level number)
        maxLevelToFind = prevLevel - 1;

        // If we've checked level 1, we're done
        if (prevLevel === 1) {
          break;
        }
      }
    }

    return true;
  }

  /**
   * Validate calculated form values against original API data
   * Checks if calculated totals match the API extracted values
   * Stores errors in validationErrors Map
   */
  private validateAgainstApiData(): void {
    const columns: { key: string; label: string }[] = [];
    if (this.showConsolidatedCurrent)  columns.push({ key: 'Consolidated|Current',  label: 'Consolidated Current' });
    if (this.showConsolidatedPrevious) columns.push({ key: 'Consolidated|Previous', label: 'Consolidated Previous' });
    if (this.showSeparateCurrent)      columns.push({ key: 'Separate|Current',      label: 'Separate Current' });
    if (this.showSeparatePrevious)     columns.push({ key: 'Separate|Previous',     label: 'Separate Previous' });

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
      controls: this.schema.controls,
      columns,
      getControlName: (qname, key) => {
        const [cs, period] = key.split('|') as ['Consolidated' | 'Separate', 'Current' | 'Previous'];
        return this.getXbrlControlName(qname, cs, period);
      },
      getCurrentValue: (name) => this.form.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema.controls, warnings);
  }

  /**
   * Get validation status for a row (aggregates status from all 4 columns)
   * Status priority: Failed > Edited > Validated* > Validated
   * Note: There should be NO empty status - every row must show a status
   */
  getRowValidationStatus(
    control: SchemaControl
  ): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    this.viewVersion();
    if (!this.isMonetaryItem(control)) return 'Validated';

    if (this.isRowInEditMode(control)) {
      const frozen = this.frozenRowStatus.get(control.qname);
      if (frozen !== undefined) return frozen;
    }

    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    const currentValues: Record<string, unknown> = {};
    for (const controlName of controlNames) {
      const fc = this.form.get(controlName);
      if (fc) {
        currentValues[controlName] = fc.value;
      }
    }

    // Unmasked blocking-error verdict + suggested-change warning, both computed
    // WITHOUT the edit-mode (forceEdited) masking. The `forceEdited` display
    // masking is applied below by computeRowStatus, but the underlying flags
    // ignore edit mode — see _computeRowErrorFlags() / rowHasBlockingError().
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(
        SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
      ),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
      // Calculated/readonly totals are computed client-side and are absent from
      // the persisted baseline, so the deviation check would mark them Edited with
      // zero user edits. They are never user-editable (enterEditMode skips them) —
      // mark them derived to skip the Edited branch.
      derived: this.isCalculatedField(control),
    });
  }

  /**
   * Compute the row's hasError / hasSuggestedChangeWarning flags. This is the
   * single definition of "does the row have a hard error" vs "only a
   * suggested-change warning" — consumed by both the Mapping Status (which
   * layers the Edited override on top via computeRowStatus) and the row colour
   * intent (which deliberately ignores edit mode). Keeping it in one place means
   * the red/yellow background can never disagree with the Failed/Validated* badge.
   *
   * `hasError` delegates to {@link rowHasBlockingError} (the unmasked verdict,
   * also used by evaluateSheetStatus) so cross-sheet equality mismatches,
   * required-empty cells and invalid form controls all count as hard errors.
   */
  private _computeRowErrorFlags(
    control: SchemaControl
  ): { hasError: boolean; hasSuggestedChangeWarning: boolean } {
    const hasError = this.rowHasBlockingError(control);

    // Suggested-Change warning (all errors are "does not match the extracted sum", no linkTo suppression needed here)
    let hasSuggestedChangeWarning = false;
    if (this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      const allAreSuggestedChanges = allErrors.every((message) =>
        message.includes('does not match the extracted sum')
      );
      hasSuggestedChangeWarning = allAreSuggestedChanges && !control.ui.linkTo;
    }

    return { hasError, hasSuggestedChangeWarning };
  }

  /**
   * Row background colour intent: 'error' (red, `row-has-error`) when the row
   * has a hard error, 'warning' (yellow, `row-has-validation-error`) when it
   * only has a suggested-change warning, 'none' otherwise. Red outranks yellow.
   *
   * Unlike the Mapping Status this does NOT consult edit mode, so clicking the
   * edit pencil never repaints the row. Role 9 is uncached (the template calls
   * helper methods directly), so this getter just delegates to
   * _computeRowColorIntent(); viewVersion() is read for OnPush re-evaluation
   * parity with the sibling row helpers.
   */
  getRowColorIntent(control: SchemaControl): 'error' | 'warning' | 'none' {
    this.viewVersion();
    return this._computeRowColorIntent(control);
  }

  /**
   * Compute the row background colour intent (full logic). Mirrors
   * getRowValidationStatus's early-return guard, then resolves error → warning →
   * none from the shared {@link _computeRowErrorFlags}.
   */
  private _computeRowColorIntent(control: SchemaControl): 'error' | 'warning' | 'none' {
    if (!this.isMonetaryItem(control)) return 'none';
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);
    if (hasError) return 'error';
    if (hasSuggestedChangeWarning) return 'warning';
    return 'none';
  }

  /**
   * Unmasked blocking-error verdict for a row — the raw `hasError` boolean
   * BEFORE the `forceEdited` edit-mode display masking that
   * {@link getRowValidationStatus} applies via `computeRowStatus`. Used by
   * {@link evaluateSheetStatus} so an in-edit-mode row carrying a real
   * cross-sheet equality mismatch (the editable `BiologicalAssets` ≠ the
   * Role 8 sum) or an empty required cell still flips the sheet verdict to
   * `hasErrors`, while the editor keeps showing the 'Edited' pencil. Mirrors
   * the role 23 / role 24 verdict-masking fix: the pure validator never masks,
   * so without this the headless and mounted verdicts diverge during editing.
   */
  private rowHasBlockingError(control: SchemaControl): boolean {
    if (!this.isMonetaryItem(control)) return false;

    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    // Only a VISIBLE column may paint the row. A column this permutation does not
    // render can never SHOW a message — every message surface on this sheet
    // filters hidden columns out — so counting one here produces a red row with an
    // empty Error Description. That is the UAT defect of 2026-08-26 (role 18's
    // "Cash and cash equivalents at beginning / end of period" rows, opened on a
    // filing with no previous year): `enable()` makes a hidden, permanently-null
    // required cell invalid, and the reveal on row-open then marks it touched.
    //
    // Developer Note (TODO): `hasValue` below is deliberately still read across
    // ALL FOUR columns. Narrowing it to the visible ones is a separate, correct
    // change — a mandatory row whose only value sits in a hidden column IS
    // unsatisfied — but it can only ADD red, so it must not land until this
    // sheet's required-message builder is confirmed to emit a per-visible-column
    // mandatory line for that case. Otherwise it recreates the very defect above,
    // in the other direction. Role 18 DOES narrow both halves — its
    // `validateRequiredFields()` was made the last writer of the per-column
    // mandatory partition on 2026-08-26, so its red is always accompanied by
    // text; that confirmation is what this sheet still owes.
    const columnVisible: boolean[] = [
      this.showConsolidatedCurrent,
      this.showConsolidatedPrevious,
      this.showSeparateCurrent,
      this.showSeparatePrevious,
    ];

    let hasInvalid = false;
    let hasValue = false;
    controlNames.forEach((controlName, index) => {
      const fc = this.form.get(controlName);
      if (fc) {
        if (columnVisible[index] && fc.invalid && (fc.dirty || fc.touched)) hasInvalid = true;
        if (fc.value !== null && fc.value !== undefined && fc.value !== '') hasValue = true;
      }
    });

    const isRequired = control.required === true;
    let hasError = hasInvalid || (isRequired && !hasValue);
    if (!hasError && this.validationErrors.has(control.qname)) {
      const errors = this.validationErrors.get(control.qname) || [];
      hasError = errors.some((message) => !message.includes('does not match the extracted sum'));
    }
    return hasError;
  }

  /**
   * Get validation icon class
   */
  getValidationIcon(
    status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'
  ): string {
    switch (status) {
      case 'Validated':
        return 'pi pi-check-circle';
      case 'Validated*':
        return 'pi pi-check-circle';
      case 'Failed':
        return 'pi pi-times-circle';
      case 'Edited':
        return 'pi pi-pencil';
      default:
        return '';
    }
  }

  /**
   * Get validation icon color
   */
  getValidationIconColor(
    status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'
  ): string {
    switch (status) {
      case 'Validated':
        return 'green';
      case 'Validated*':
        return '#D97706'; // Amber color for warning severity
      case 'Failed':
        return 'red';
      case 'Edited':
        return 'orange';
      default:
        return '';
    }
  }

  /**
   * Get error description for a row (aggregates from all 4 columns)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   */
  getRowErrorDescription(control: SchemaControl): string {
    this.viewVersion();
    if (!this.isMonetaryItem(control)) return '';

    const errors: string[] = [];           // Red - Error category (form control errors)
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (API mismatch)

    // Column visibility filter helper
    const isVisibleColumn = (error: string): boolean => {
      if (error.includes('Consolidated Current') && !this.showConsolidatedCurrent) return false;
      if (error.includes('Consolidated Previous') && !this.showConsolidatedPrevious) return false;
      if (error.includes('Separate Current') && !this.showSeparateCurrent) return false;
      if (error.includes('Separate Previous') && !this.showSeparatePrevious) return false;
      return true;
    };

    // 1. Process API data mismatch errors -> Suggested Change category
    const validationErrors = this.validationErrors.get(control.qname);
    if (validationErrors && validationErrors.length > 0) {
      validationErrors.forEach((errorMsg) => {
        if (!isVisibleColumn(errorMsg)) return;

        if (errorMsg.includes('does not match the extracted sum')) {
          suggestedChanges.push(errorMsg);
        } else {
          errors.push(errorMsg);
        }
      });
    }

    // 2. Collect form validation errors -> Error category (only for visible columns)
    const columnsToCheck: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
      label: string;
      show: boolean;
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current', label: 'Consolidated Current', show: this.showConsolidatedCurrent },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous', label: 'Consolidated Previous', show: this.showConsolidatedPrevious },
      { consolidatedOrSeparate: 'Separate', period: 'Current', label: 'Separate Current', show: this.showSeparateCurrent },
      { consolidatedOrSeparate: 'Separate', period: 'Previous', label: 'Separate Previous', show: this.showSeparatePrevious },
    ];

    for (const { consolidatedOrSeparate, period, label, show } of columnsToCheck) {
      if (!show) continue;

      const controlName = this.getXbrlControlName(
        control.qname,
        consolidatedOrSeparate,
        period
      );
      const formControl = this.form.get(controlName);

      if (
        formControl &&
        formControl.invalid &&
        (formControl.dirty || formControl.touched)
      ) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          if (formControl.errors['required'])
            errorMessages.push('Required field');
          if (formControl.errors['min'])
            errorMessages.push(`Min: ${formControl.errors['min'].min}`);
        }

        if (errorMessages.length > 0) {
          errors.push(`${label}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // 3. Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
    // computeMismatchWarnings call exactly (same visible columns + composite keys).
    if (this.baselineDebug.enabled()) {
      const columns: { key: string; label: string }[] = [];
      if (this.showConsolidatedCurrent)  columns.push({ key: 'Consolidated|Current',  label: 'Consolidated Current' });
      if (this.showConsolidatedPrevious) columns.push({ key: 'Consolidated|Previous', label: 'Consolidated Previous' });
      if (this.showSeparateCurrent)      columns.push({ key: 'Separate|Current',      label: 'Separate Current' });
      if (this.showSeparatePrevious)     columns.push({ key: 'Separate|Previous',     label: 'Separate Previous' });
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
        control as unknown as SummableControl,
        columns,
        (qname, key) => {
          const [cs, period] = key.split('|') as ['Consolidated' | 'Separate', 'Current' | 'Previous'];
          return this.getXbrlControlName(qname, cs, period);
        }
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Check if row has validation errors (for styling)
   */
  hasRowValidationError(control: SchemaControl): boolean {
    this.viewVersion();
    if (!this.isMonetaryItem(control)) return false;
    return this.validationErrors.has(control.qname);
  }

  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;
    for (const control of this.schema.controls) {
      // Use the unmasked verdict — getRowValidationStatus() would report
      // 'Edited' (not 'Failed') for an in-edit-mode row, hiding a real
      // BiologicalAssets ≠ Role 8 sum mismatch from the sheet status.
      if (this.rowHasBlockingError(control)) {
        hasErrors = true;
        break;
      }
    }

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity, { hasErrors });
  }

  /**
   * Check if row has Error category items (for red text styling)
   * Error category includes: form control errors
   */
  hasErrorCategory(control: SchemaControl): boolean {
    // Check for form control validation errors
    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
      show: boolean;
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current', show: this.showConsolidatedCurrent },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous', show: this.showConsolidatedPrevious },
      { consolidatedOrSeparate: 'Separate', period: 'Current', show: this.showSeparateCurrent },
      { consolidatedOrSeparate: 'Separate', period: 'Previous', show: this.showSeparatePrevious },
    ];

    for (const { consolidatedOrSeparate, period, show } of columns) {
      if (!show) continue;

      const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, period);
      const formControl = this.form.get(controlName);

      if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if row has Suggested Change category items (for black text styling)
   * Suggested Change category includes: API mismatch warnings
   */
  hasSuggestedChangeCategory(control: SchemaControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const allErrors = this.validationErrors.get(control.qname) || [];

    return allErrors.some((errorMsg) => {
      if (errorMsg.includes('Consolidated Current') && !this.showConsolidatedCurrent) return false;
      if (errorMsg.includes('Consolidated Previous') && !this.showConsolidatedPrevious) return false;
      if (errorMsg.includes('Separate Current') && !this.showSeparateCurrent) return false;
      if (errorMsg.includes('Separate Previous') && !this.showSeparatePrevious) return false;

      return errorMsg.includes('does not match the extracted sum');
    });
  }

  /**
   * Check if a row is currently in edit mode
   */
  isRowInEditMode(control: SchemaControl): boolean {
    this.viewVersion();
    return this.editingRows.has(control.qname);
  }

  /**
   * Enter edit mode for a row - store original values, enable controls
   */
  enterEditMode(control: SchemaControl): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete()) {
      return;
    }
    // Don't allow editing calculated or readonly fields
    if (this.isCalculatedField(control) || control.ui.readonly) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(control.qname, this.getRowValidationStatus(control));

    // Store original values for all 4 controls
    const originalValues: { [key: string]: number | null } = {};
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    controlNames.forEach((name) => {
      const formControl = this.form.get(name);
      if (formControl) {
        originalValues[name] = formControl.value ?? null;
      }
    });

    // Enable all 4 controls, revealing only the columns this permutation SHOWS —
    // a hidden required column is permanently null, so enabling makes it invalid
    // and revealing it would paint the row red with no text to explain it.
    enableDualColumnRowAndRevealVisible(
      this.form,
      control.qname,
      this.dimensionVisibility,
      { emitEvent: true }
    );

    this.editingRows.set(control.qname, originalValues);

    // Mark the sheet as actively editing so cross-tab / headless consumers know
    // an uncommitted edit is in progress until the Tick (saveEditMode) clears it.
    this.stateService.markSheetEditing(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);
  }

  /**
   * Save edit mode (Tick) — disable controls, subset-persist, validate, reconcile, clear editing.
   * Cross-sheet (equality) validation runs here only, not during live edit.
   */
  saveEditMode(control: SchemaControl): void {
    if (!this.editingRows.has(control.qname)) {
      return;
    }

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    // Check if any control has validation errors
    const hasErrors = controlNames.some((name) => {
      const formControl = this.form.get(name);
      return formControl && formControl.invalid;
    });

    if (hasErrors) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix validation errors before saving',
      });
      return;
    }

    // Disable the row's controls and exit edit mode FIRST so the subset we
    // persist reflects the committed (raw) values.
    controlNames.forEach((name) => {
      const formControl = this.form.get(name);
      if (formControl) {
        formControl.disable({ emitEvent: false });
      }
    });
    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    // Commit-time validation sequence: recompute totals FIRST so the persisted
    // subset includes the freshly recalculated calculated/derived totals (the
    // downstream sheets read these), then persist, then refresh verdicts.
    this.calculateTotals();

    // Subset persist: write this row's committed cells PLUS the recalculated
    // calculated totals. updateSheetData's shallow merge leaves every other
    // (possibly mid-edit elsewhere) field at its committed value — this is the
    // commit-on-Tick boundary.
    const subset = pickSubset(this.form.getRawValue(), [...controlNames, ...this.calculatedTotalKeys]);
    this.stateService.updateSheetData(
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
      subset
    );

    // Commit-time validation sequence: refresh the same-sheet verdict, then run
    // the cross-sheet (equality) pass — the latter only happens here.
    this.validateSameSheet();
    this.validateCrossSheet();

    // Reconcile the edited flag against the committed baseline (a revert-to-original
    // + Tick clears the pencil), then release the editing lock and re-publish status.
    this.stateService.reconcileEditedFlag(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);
    this.stateService.clearSheetEditing(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - revert to original values if not previously saved, otherwise keep current values
   * - If user didn't trigger saveEditMode previously, revert to the initial values stored when entering edit mode
   * - If user triggered saveEditMode before, keep the changed values
   */
  cancelEditMode(control: SchemaControl): void {
    if (!this.editingRows.has(control.qname)) {
      return;
    }

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const entryValues = this.editingRows.get(control.qname);
    const adopted = this.stateService.getSheetData(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);
    Object.keys(entryValues ?? {}).forEach((controlName) => {
      const formControl = this.form.get(controlName);
      if (formControl) {
        const revertValue = controlName in adopted
          ? adopted[controlName]
          : (entryValues?.[controlName] ?? null);
        formControl.setValue(revertValue, { emitEvent: false });
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    // Cancel discards uncommitted typing — only the same-sheet verdict can change
    // (totals reverted). The cross-sheet verdict is unchanged since nothing
    // committed, so re-run the same-sheet pass only.
    this.calculateTotals();
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);
    this.evaluateSheetStatus();
  }

  /**
   * Run JSON-driven equality validation
   * Uses EqualityValidationService to validate equality rules defined in form JSON
   */
  private runEqualityValidations(): void {
    if (!this.schema) {
      return;
    }

    // Detect taxonomy (MFRS or MPERS)
    const isMFRS = this.schema.controls.some(
      (control) => control.qname === 'ifrs-full:BiologicalAssets'
    );
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';

    // Get data from all sheets via state service (for cross-sheet validation)
    const allSheetData = this.stateService.exportAllData();

    // Merge with current form values (ensures latest unsaved changes are included)
    const formValuesWithAllSheets = {
      ...allSheetData,
      'subClassificationOfAssetsLiabilitiesEquity': this.form.getRawValue()
    };

    // Pass 'subClassificationOfAssetsLiabilitiesEquity' as currentSheetKey
    const errors = this.equalityValidation.validateAllEqualityRules(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
    );

    // Store errors
    this.equalityValidationErrors = new Map(Object.entries(errors));

    // Also add to validationErrors Map for display compatibility
    Object.keys(errors).forEach(qname => {
      const errorMessages = errors[qname].map(err => err.message);
      const existingErrors = this.validationErrors.get(qname) || [];
      // Keep only API mismatch warnings (contain EXTRACTED_SUM_MARKER)
      const apiMismatchWarnings = existingErrors.filter(msg =>
        msg.includes(EXTRACTED_SUM_MARKER)
      );
      this.validationErrors.set(qname, [...apiMismatchWarnings, ...errorMessages]);
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('✅ Equality validation completed (role9):', {
    //   errorsFound: this.equalityValidationErrors.size,
    //   errors: Object.fromEntries(this.equalityValidationErrors)
    // });

  }

  /**
   * Does THIS cell carry a blocking error, so the template can paint just that
   * input red rather than leaving the row's four columns indistinguishable?
   *
   * P.O. 2026-08-26: the red border appears whenever the field's error text
   * appears. This sheet had no per-cell binding at all — a row went red across
   * every column with nothing to say which one was wrong. `BiologicalAssets` is
   * what surfaced it: the sheet's only `equality`-carrying control and its only
   * non-`required` total, so an equality message was its one route to a red row
   * and there was nowhere for it to paint.
   *
   * Scope mirrors {@link getRowErrorDescription} exactly, so border and text
   * cannot disagree:
   *  - the `does not match the extracted sum` family is a suggested-change
   *    WARNING (yellow row via `_computeRowColorIntent`) and must NOT paint red;
   *  - required-empty is not covered because this sheet emits no per-column
   *    mandatory message — requiredness feeds a row-level predicate only, so
   *    there is no text to pair a border with.
   *
   * Column scoping uses the same `.includes(label)` test as
   * `getRowErrorDescription`'s `isVisibleColumn`, so both read the producers'
   * message format the same way.
   */
  hasCellError(
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): boolean {
    this.viewVersion();
    const errors = this.validationErrors.get(qname);
    if (!errors || errors.length === 0) return false;

    const label = `${consolidatedOrSeparate} ${period}`;
    return errors.some(
      (msg) => msg.includes(label) && !msg.includes('does not match the extracted sum')
    );
  }

  /**
   * Check if a field has equality validation error
   */
  hasEqualityError(qname: string): boolean {
    return this.equalityValidationErrors.has(qname);
  }

  /**
   * Get equality validation error message for a field
   */
  getEqualityErrorMessage(qname: string): string {
    const errors = this.equalityValidationErrors.get(qname);
    if (!errors || errors.length === 0) return '';
    return errors.map(err => err.message).join('\n');
  }

  onCancelConfirmed(): void {
    // Clear all editing states (R5b: clear editing flag so pure-substitution bypass lifts)
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity);

    // Reload from the reverted state service data
    this.isValidationComplete.set(false);
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.form.markAsPristine();
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: SchemaControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
